import { useState, useEffect, useRef } from "react";
import Landing from "./Landing";

// ─── DATA ──────────────────────────────────────────────────────────────────────

const AGE_GROUPS = [
  { id: "little", label: "Explorers", range: "Ages 5–8", emoji: "🚀", color: "#F97316", bg: "#FFF7ED", maxSession: 20 },
  { id: "middle", label: "Builders", range: "Ages 9–12", emoji: "⚙️", color: "#0D9488", bg: "#F0FDFA", maxSession: 30 },
  { id: "teen", label: "Innovators", range: "Ages 13–17", emoji: "💡", color: "#7C3AED", bg: "#F5F3FF", maxSession: 45 },
];

const OFFLINE_ACTIVITIES = {
  little: [
    { emoji: "🖍️", title: "Draw Your Program", desc: "Grab paper and crayons! Draw the loop you just learned as a dance the characters do. How many times do they spin?", type: "creative", mins: 15 },
    { emoji: "🌿", title: "Nature Scavenger Hunt", desc: "Go outside and find things that come in groups of 3! Rocks, leaves, sticks — make a list like a real array.", type: "nature", mins: 20 },
    { emoji: "🤸", title: "Human Loop", desc: "Jump 5 times, clap 3 times, spin once — then repeat! You just ran a for loop with your body.", type: "active", mins: 10 },
    { emoji: "📖", title: "Story Time", desc: "Pick a favourite book. Count how many times the main character makes a decision — those are if/else moments!", type: "reading", mins: 20 },
    { emoji: "🧩", title: "Sequence Puzzle", desc: "Build something with LEGO or blocks. Make instructions for someone else to build the same thing — that's an algorithm!", type: "creative", mins: 25 },
    { emoji: "🌱", title: "Garden Explorer", desc: "Go find a plant outside. How does it grow — what pattern does it follow? Patterns are loops in nature!", type: "nature", mins: 15 },
    { emoji: "🎲", title: "Board Game Logic", desc: "Play any board game. Notice all the if/else rules — 'IF you land here, THEN do this.' Games are programs!", type: "active", mins: 30 },
    { emoji: "✏️", title: "Robot Instructions", desc: "Give someone else step-by-step instructions to make a sandwich without using your hands. Be a human algorithm!", type: "creative", mins: 15 },
  ],
  middle: [
    { emoji: "📓", title: "Code Journal", desc: "Write out today's program BY HAND in your notebook. Studies show handwriting cements memory far better than typing.", type: "creative", mins: 20 },
    { emoji: "🚴", title: "Algorithm Run", desc: "Go for a bike ride or walk. Think about your route as an algorithm — what decisions did you make? Could you optimize it?", type: "active", mins: 30 },
    { emoji: "🌳", title: "Pattern Hunt in Nature", desc: "Fibonacci sequences appear in pinecones, sunflowers, and shells. Go find one! Nature runs the best algorithms.", type: "nature", mins: 20 },
    { emoji: "🎮", title: "Unplug & Design", desc: "Design a game on paper — no screens. What are the rules? Write them as if/else statements. Game designers think in code.", type: "creative", mins: 25 },
    { emoji: "🍳", title: "Cooking Algorithm", desc: "Help make a meal or snack. Write the recipe as a proper algorithm with loops ('stir 30 times') and conditions ('if too hot, lower heat').", type: "active", mins: 30 },
    { emoji: "🌦️", title: "Weather Data Collector", desc: "Start a physical weather journal. Record temp, conditions, cloud type each day. You're building a real dataset — by hand!", type: "nature", mins: 10 },
    { emoji: "🏀", title: "Sport Science Break", desc: "Play your favourite sport for 30 minutes. Notice the patterns — basketball plays, soccer formations. Patterns are everywhere.", type: "active", mins: 30 },
    { emoji: "🗺️", title: "Neighbourhood Map", desc: "Draw a map of your street or neighbourhood from memory. Add decision points — 'IF it's raining, take this path.' Real-world GPS thinking!", type: "creative", mins: 25 },
  ],
  teen: [
    { emoji: "📔", title: "Rubber Duck Journaling", desc: "Explain today's hardest concept in writing as if talking to a rubber duck. If you can explain it, you truly understand it.", type: "creative", mins: 20 },
    { emoji: "🏃", title: "Decompress & Debug", desc: "Go for a 20-minute run or walk — no phone. Unsolved problems often crack during physical movement. Come back fresh.", type: "active", mins: 25 },
    { emoji: "🌲", title: "Sit Spot", desc: "Find a spot outside, sit still for 10 minutes, no phone. Observe what changes. Presence and patience are superpower skills for developers.", type: "nature", mins: 15 },
    { emoji: "📚", title: "Read a Chapter", desc: "Pick up a physical book — fiction or non-fiction. Reading builds the deep focus and imagination that makes great programmers.", type: "reading", mins: 30 },
    { emoji: "🎸", title: "Create Something Offline", desc: "Draw, write music, sketch an app UI on paper, journal. Analog creativity recharges the digital mind in ways screens can't.", type: "creative", mins: 30 },
    { emoji: "🌊", title: "Nature Reset", desc: "Water, trees, open sky — 20 minutes outside without a device resets cortisol levels and boosts problem-solving capacity by up to 20%.", type: "nature", mins: 20 },
    { emoji: "🧘", title: "Breathwork Break", desc: "Box breathing: inhale 4 sec, hold 4 sec, exhale 4 sec, hold 4 sec. Repeat 4 times. Resets your nervous system for better focus.", type: "active", mins: 10 },
    { emoji: "🗣️", title: "Teach It", desc: "Explain what you learned today to a family member, friend, or pet. The 'protégé effect' — teaching = the deepest form of learning.", type: "creative", mins: 15 },
  ],
};

const MODULES = {
  little: [
    { id: 1, title: "Giving Instructions", type: "concept", xp: 50 },
    { id: 2, title: "Yes or No Decisions", type: "concept", xp: 60 },
    { id: 3, title: "My First Loop", type: "build", xp: 80, comingSoon: true },
    { id: 4, title: "Ask the Helper", type: "ai", xp: 70, comingSoon: true },
  ],
  middle: [
    { id: 1, title: "For Loops", type: "concept", xp: 80 },
    { id: 2, title: "If/Elif/Else", type: "concept", xp: 80 },
    { id: 3, title: "Variables & Memory", type: "concept", xp: 80, comingSoon: true },
    { id: 4, title: "Functions & Reuse", type: "build", xp: 120, comingSoon: true },
    { id: 5, title: "Prompting Basics", type: "ai", xp: 90, comingSoon: true },
    { id: 6, title: "Debug with AI", type: "ai", xp: 110, comingSoon: true },
  ],
  teen: [
    { id: 1, title: "Functions", type: "concept", xp: 100 },
    { id: 2, title: "Lists and Loops", type: "concept", xp: 120 },
    { id: 3, title: "Python Deep Dive", type: "concept", xp: 100, comingSoon: true },
    { id: 4, title: "Data Structures", type: "concept", xp: 120, comingSoon: true },
    { id: 5, title: "Algorithms 101", type: "concept", xp: 140, comingSoon: true },
    { id: 6, title: "APIs & Claude", type: "ai", xp: 160, comingSoon: true },
    { id: 7, title: "Prompt Engineering", type: "ai", xp: 150, comingSoon: true },
    { id: 8, title: "Build a Real App", type: "build", xp: 200, comingSoon: true },
  ],
};

// Derive module status from user progress: first incomplete module is active, subsequent ones are locked
function getModulesWithStatus(ageId, progress) {
  const raw = MODULES[ageId] || MODULES.middle;
  let foundActive = false;
  return raw.map(mod => {
    const moduleKey = `${ageId}-${mod.id}`;
    const isComplete = progress.completedModules.includes(moduleKey);
    const brainScore = progress.brainScores[moduleKey] || null;
    if (isComplete) return { ...mod, complete: true, brainScore };
    if (mod.comingSoon) return { ...mod, locked: true, brainScore: null };
    if (!foundActive) {
      foundActive = true;
      return { ...mod, complete: false, active: true, brainScore: null };
    }
    return { ...mod, locked: true, brainScore: null };
  });
}

const EXPLORER_CORRECT_ORDER = ["Walk forward", "Turn right", "Pick up star", "Stop"];

const TYPE_COLORS = { nature: "#059669", active: "#F97316", creative: "#7C3AED", reading: "#0D9488" };
const TYPE_BG = { nature: "#ECFDF5", active: "#FFF7ED", creative: "#F5F3FF", reading: "#F0FDFA" };
const TYPE_LABEL = { nature: "🌿 Nature", active: "🏃 Get Moving", creative: "🎨 Create", reading: "📚 Read" };

// ─── PROGRESS PERSISTENCE ──────────────────────────────────────────────────────

const DEFAULT_PROGRESS = {
  xp: 0,
  completedModules: [],
  brainScores: {},
  offlineActivities: [],
};

function saveProgress(data) {
  try { localStorage.setItem("sproutcode_progress", JSON.stringify(data)); } catch {}
}

function loadProgress() {
  try {
    const raw = localStorage.getItem("sproutcode_progress");
    return raw ? { ...DEFAULT_PROGRESS, ...JSON.parse(raw) } : null;
  } catch { return null; }
}

// ─── HELPERS ───────────────────────────────────────────────────────────────────

function Badge({ color, bg, children, small }) {
  return (
    <span style={{
      background: bg, color, borderRadius: 99,
      padding: small ? "2px 8px" : "3px 11px",
      fontSize: small ? 10 : 11, fontWeight: 700, letterSpacing: 0.3,
      border: `1px solid ${color}25`, whiteSpace: "nowrap"
    }}>{children}</span>
  );
}

function BrainMeter({ score }) {
  if (!score) return null;
  const color = score > 75 ? "#10B981" : score > 50 ? "#F59E0B" : "#F97316";
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#78716C", marginBottom: 3 }}>
        <span>🧠 Brain Score</span>
        <span style={{ fontWeight: 700, color }}>{score}%</span>
      </div>
      <div style={{ background: "#E7E5E4", borderRadius: 99, height: 5 }}>
        <div style={{ width: `${score}%`, background: color, height: "100%", borderRadius: 99, transition: "width 1s" }} />
      </div>
    </div>
  );
}

function SessionBar({ elapsed, max, color }) {
  const pct = Math.min((elapsed / max) * 100, 100);
  const barColor = pct > 80 ? "#EF4444" : pct > 60 ? "#F59E0B" : "#10B981";
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#78716C", marginBottom: 5 }}>
        <span>⏱️ Session Time</span>
        <span style={{ fontWeight: 700, color: barColor }}>{elapsed}m / {max}m</span>
      </div>
      <div style={{ background: "#E7E5E4", borderRadius: 99, height: 8, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, background: barColor, height: "100%", borderRadius: 99, transition: "width 1s ease" }} />
      </div>
    </div>
  );
}

// ─── LESSON PROGRESS BAR ───────────────────────────────────────────────────────

function LessonProgress({ phase, color, onStepClick }) {
  const steps = ["watch", "think", "check", "celebrate"];
  const labels = { watch: "WATCH", think: "THINK", check: "CHECK", celebrate: "CELEBRATE" };
  const activeIdx = steps.indexOf(phase);
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 24, padding: "0 4px" }}>
      {steps.map((step, i) => {
        const isActive = step === phase;
        const isPast = i < activeIdx;
        const canClick = isPast && !!onStepClick;
        const dotBg = isActive ? color : isPast ? `${color}99` : "#D6D3D1";
        const textColor = isActive ? color : isPast ? `${color}99` : "#A8A29E";
        return (
          <div key={step} style={{ display: "flex", alignItems: "center", flex: i < steps.length - 1 ? 1 : "none" }}>
            <div
              onClick={canClick ? () => onStepClick(step) : undefined}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: canClick ? "pointer" : "default" }}
            >
              <div style={{
                width: 28, height: 28, borderRadius: "50%", background: dotBg, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800,
                fontSize: 12, flexShrink: 0,
                boxShadow: isActive ? `0 0 0 3px ${color}33` : canClick ? `0 0 0 2px ${color}55` : "none",
                transition: "box-shadow 0.15s",
              }}>
                {isPast ? "✓" : i + 1}
              </div>
              <span style={{ fontSize: 9, fontWeight: 700, color: textColor, letterSpacing: 0.5, whiteSpace: "nowrap", textDecoration: canClick ? "underline" : "none" }}>
                {labels[step]}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, background: isPast ? `${color}99` : "#E7E5E4", margin: "0 6px", marginBottom: 18 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── OFFLINE ACTIVITY SCREEN ───────────────────────────────────────────────────

function OfflineScreen({ ageId, onUnlock }) {
  const activities = OFFLINE_ACTIVITIES[ageId] || OFFLINE_ACTIVITIES.middle;
  const [chosen, setChosen] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);
  const intervalRef = useRef(null);

  // Pick 3 random activities on mount
  const [suggestions] = useState(() => {
    const shuffled = [...activities].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
  });

  const startActivity = (act) => {
    setChosen(act);
    setElapsed(0);
    intervalRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
  };

  const completeActivity = () => {
    clearInterval(intervalRef.current);
    setDone(true);
  };

  const progressPct = chosen ? Math.min((elapsed / (chosen.mins * 60)) * 100, 100) : 0;
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;

  return (
    <div style={{
      minHeight: "100vh", background: "linear-gradient(160deg, #052e16 0%, #14532d 40%, #1a3a2a 100%)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "32px 20px", fontFamily: "'Trebuchet MS', sans-serif"
    }}>
      {!chosen ? (
        <div style={{ maxWidth: 560, width: "100%", textAlign: "center" }}>
          {/* Header */}
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🌿</div>
            <h1 style={{
              fontSize: 32, fontWeight: 900, color: "#ffffff",
              fontFamily: "'Georgia', serif", margin: "0 0 12px", lineHeight: 1.2
            }}>Time for a Real-World Break!</h1>
            <p style={{ fontSize: 16, color: "#86efac", lineHeight: 1.7, margin: 0 }}>
              Your screen time for today is up — amazing work! 🎉<br />
              Your brain needs offline time to lock in what you learned.<br />
              <strong style={{ color: "#fff" }}>Pick an activity and come back refreshed.</strong>
            </p>
            <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
              <Badge color="#10B981" bg="#052e16">🔒 Next session unlocks after your break</Badge>
            </div>
          </div>

          {/* Activity Cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 28 }}>
            {suggestions.map((act, i) => (
              <button key={i} onClick={() => startActivity(act)} style={{
                background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 18, padding: "20px 22px", cursor: "pointer", textAlign: "left",
                transition: "all 0.2s", backdropFilter: "blur(8px)"
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.13)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.transform = "none"; }}
              >
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 36, flexShrink: 0 }}>{act.emoji}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>{act.title}</span>
                      <span style={{
                        background: TYPE_BG[act.type], color: TYPE_COLORS[act.type],
                        borderRadius: 99, padding: "1px 8px", fontSize: 10, fontWeight: 700
                      }}>{TYPE_LABEL[act.type]}</span>
                    </div>
                    <p style={{ fontSize: 13, color: "#a7f3d0", lineHeight: 1.6, margin: 0 }}>{act.desc}</p>
                    <div style={{ marginTop: 8, fontSize: 12, color: "#6ee7b7" }}>⏱ ~{act.mins} minutes</div>
                  </div>
                  <div style={{
                    background: "#10B981", color: "#fff", borderRadius: 10,
                    padding: "6px 14px", fontSize: 12, fontWeight: 700, flexShrink: 0, alignSelf: "center"
                  }}>Start →</div>
                </div>
              </button>
            ))}
          </div>

          <button onClick={() => startActivity(suggestions[Math.floor(Math.random() * suggestions.length)])} style={{
            background: "none", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 10,
            color: "#86efac", fontSize: 13, padding: "8px 18px", cursor: "pointer"
          }}>🎲 Surprise me with one</button>
        </div>
      ) : !done ? (
        // Activity Timer
        <div style={{ maxWidth: 460, width: "100%", textAlign: "center" }}>
          <div style={{ fontSize: 72, marginBottom: 20 }}>{chosen.emoji}</div>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: "#fff", fontFamily: "'Georgia', serif", margin: "0 0 12px" }}>{chosen.title}</h2>
          <p style={{ fontSize: 15, color: "#a7f3d0", lineHeight: 1.7, marginBottom: 28 }}>{chosen.desc}</p>

          {/* Timer circle */}
          <div style={{
            width: 160, height: 160, borderRadius: "50%", margin: "0 auto 28px",
            background: `conic-gradient(#10B981 ${progressPct * 3.6}deg, rgba(255,255,255,0.1) 0deg)`,
            display: "flex", alignItems: "center", justifyContent: "center", position: "relative"
          }}>
            <div style={{
              width: 130, height: 130, borderRadius: "50%", background: "#052e16",
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center"
            }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", fontFamily: "'Georgia', serif" }}>
                {String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
              </div>
              <div style={{ fontSize: 11, color: "#86efac" }}>of {chosen.mins}:00</div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <button onClick={completeActivity} style={{
              background: "#10B981", color: "#fff", border: "none", borderRadius: 14,
              padding: "14px 32px", cursor: "pointer", fontSize: 16, fontWeight: 800
            }}>✅ I did it! Unlock session</button>
            <button onClick={() => setChosen(null)} style={{
              background: "rgba(255,255,255,0.1)", color: "#a7f3d0", border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: 14, padding: "14px 20px", cursor: "pointer", fontSize: 14
            }}>← Choose another</button>
          </div>
        </div>
      ) : (
        // Completion
        <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
          <div style={{ fontSize: 72, marginBottom: 16 }}>🎉</div>
          <h2 style={{ fontSize: 28, fontWeight: 900, color: "#fff", fontFamily: "'Georgia', serif", margin: "0 0 12px" }}>
            Amazing! Real-world XP earned!
          </h2>
          <p style={{ fontSize: 15, color: "#a7f3d0", lineHeight: 1.7, marginBottom: 8 }}>
            You completed <strong style={{ color: "#fff" }}>{chosen.title}</strong>. Your brain just locked in today's coding knowledge during that offline time. That's the science — movement and nature cement memory! 🧠
          </p>
          <div style={{
            background: "rgba(255,255,255,0.08)", borderRadius: 16, padding: "18px", marginBottom: 28,
            border: "1px solid rgba(255,255,255,0.15)"
          }}>
            <div style={{ fontSize: 13, color: "#86efac", marginBottom: 8 }}>Today's offline badge earned:</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#fff" }}>{TYPE_LABEL[chosen.type]} Explorer 🏅</div>
          </div>
          <button onClick={onUnlock} style={{
            background: "#F59E0B", color: "#fff", border: "none", borderRadius: 14,
            padding: "16px 36px", cursor: "pointer", fontSize: 17, fontWeight: 800, boxShadow: "0 4px 20px #F59E0B40"
          }}>🚀 Back to coding!</button>
        </div>
      )}
    </div>
  );
}

// ─── HOME ──────────────────────────────────────────────────────────────────────

function HomeView({ onSelectAge }) {
  useEffect(() => {
    const el = document.createElement("style");
    el.id = "cs-home-responsive";
    el.textContent = `
      .cs-age-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 16px; margin-bottom: 40px; }
      @media (max-width: 767px) { .cs-age-grid { grid-template-columns: repeat(2,1fr); } }
      @media (max-width: 479px) { .cs-age-grid { grid-template-columns: 1fr; } }
    `;
    document.head.appendChild(el);
    return () => { const old = document.getElementById("cs-home-responsive"); if (old) old.remove(); };
  }, []);

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "40px 20px" }}>
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          background: "linear-gradient(135deg, #F59E0B, #F97316)", borderRadius: 24,
          padding: "18px 24px", marginBottom: 18, boxShadow: "0 8px 30px #F59E0B40"
        }}>
          <span style={{ fontSize: 44 }}>🌱</span>
        </div>
        <h1 style={{ fontFamily: "'Georgia', serif", fontSize: 44, fontWeight: 900, color: "#44403C", margin: "0 0 12px" }}>
          SproutCode
        </h1>
        <p style={{ fontSize: 17, color: "#78716C", maxWidth: 500, margin: "0 auto 20px", lineHeight: 1.75 }}>
          Real coding skills, AI as a guide — not a crutch.<br />
          Screen limits, eye breaks & nature activities baked right in.
        </p>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          <Badge color="#059669" bg="#ECFDF5">✅ Blue Light Aware</Badge>
          <Badge color="#D97706" bg="#FEF3C7">🧠 Brain-First AI</Badge>
          <Badge color="#7C3AED" bg="#F5F3FF">👪 Parent Visibility</Badge>
          <Badge color="#0D9488" bg="#F0FDFA">🌿 Offline Activities</Badge>
        </div>
      </div>

      <div className="cs-age-grid">
        {AGE_GROUPS.map(g => (
          <button key={g.id} onClick={() => onSelectAge(g.id)} style={{
            background: g.bg, border: `2px solid ${g.color}25`, borderRadius: 22,
            padding: "28px 16px", cursor: "pointer", textAlign: "center", transition: "all 0.2s",
            boxShadow: "0 2px 12px rgba(0,0,0,0.05)"
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 10px 28px ${g.color}40`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.05)"; }}
          >
            <div style={{ fontSize: 40, marginBottom: 10 }}>{g.emoji}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: g.color, fontFamily: "'Georgia', serif" }}>{g.label}</div>
            <div style={{ fontSize: 13, color: "#78716C", marginTop: 4 }}>{g.range}</div>
            <div style={{ marginTop: 10, fontSize: 12, color: "#92400E" }}>
              Max {g.maxSession} min/session
            </div>
          </button>
        ))}
      </div>

      {/* Selling point callout */}
      <div style={{
        background: "linear-gradient(135deg, #052e16, #14532d)", borderRadius: 22,
        padding: "28px 30px", color: "#fff", textAlign: "center"
      }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🌿 + 💻</div>
        <h3 style={{ fontFamily: "'Georgia', serif", fontSize: 20, margin: "0 0 12px", color: "#86efac" }}>
          Screen Time + Real Life, in Balance
        </h3>
        <p style={{ fontSize: 14, color: "#a7f3d0", lineHeight: 1.75, maxWidth: 480, margin: "0 auto" }}>
          When a session ends, SproutCode doesn't just lock out — it suggests a personalised offline activity: nature walks, drawing, cooking challenges, and more. Kids earn real-world badges alongside their coding XP. Learning doesn't stop when the screen does.
        </p>
      </div>
    </div>
  );
}

// ─── DASHBOARD ─────────────────────────────────────────────────────────────────

function DashboardView({ ageId, onNav, sessionMin, onSessionEnd, progress }) {
  const group = AGE_GROUPS.find(g => g.id === ageId);
  const modules = getModulesWithStatus(ageId, progress);
  const pct = Math.min((sessionMin / group.maxSession) * 100, 100);
  const totalXP = progress.xp;
  const brainEntries = Object.values(progress.brainScores).filter(Boolean);
  const avgBrain = brainEntries.length > 0 ? Math.round(brainEntries.reduce((a, b) => a + b, 0) / brainEntries.length) : 0;
  const typeMap = { concept: { label: "CONCEPT", color: "#0D9488", bg: "#F0FDFA" }, build: { label: "BUILD", color: "#F97316", bg: "#FFF7ED" }, ai: { label: "AI SKILLS", color: "#7C3AED", bg: "#F5F3FF" } };
  const hasCompleted = progress.completedModules.length > 0;
  const firstActive = modules.find(m => m.active);

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: "24px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <div style={{ background: group.color, borderRadius: 14, padding: "10px 14px", fontSize: 22 }}>{group.emoji}</div>
          <div>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: 20, fontWeight: 800, color: "#44403C" }}>{group.label} Dashboard</div>
            <div style={{ fontSize: 13, color: "#78716C" }}>{group.range}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => onNav("parent")} style={{ background: "#F5F3FF", color: "#7C3AED", border: "none", borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>👪 Parent View</button>
          <button onClick={onSessionEnd} style={{ background: "#FEF2F2", color: "#DC2626", border: "none", borderRadius: 10, padding: "8px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>⏱ End Session</button>
        </div>
      </div>

      {/* Session + Stats */}
      <div style={{ background: "#fff", borderRadius: 18, padding: "18px 20px", border: "1px solid #E7E5E4", marginBottom: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.04)" }}>
        <SessionBar elapsed={sessionMin} max={group.maxSession} />
        {pct > 75 && (
          <div style={{ marginTop: 10, background: "#FEF3C7", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#92400E" }}>
            ⚠️ Approaching your daily limit — finish your current task and take an offline break soon!
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12, marginBottom: 22 }}>
        <div style={{ background: "#fff", borderRadius: 14, padding: "16px 18px", border: "1px solid #E7E5E4" }}>
          <div style={{ fontSize: 22, marginBottom: 6 }}>⭐</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "#D97706", fontFamily: "'Georgia', serif" }}>{totalXP}</div>
          <div style={{ fontSize: 12, color: "#78716C" }}>XP Earned</div>
        </div>
        <div style={{ background: "#fff", borderRadius: 14, padding: "16px 18px", border: "1px solid #E7E5E4" }}>
          <div style={{ fontSize: 22, marginBottom: 6 }}>🧠</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: brainEntries.length > 0 ? (avgBrain > 75 ? "#10B981" : "#F59E0B") : "#A8A29E", fontFamily: "'Georgia', serif" }}>{brainEntries.length > 0 ? `${avgBrain}%` : "—"}</div>
          <div style={{ fontSize: 12, color: "#78716C" }}>Avg Brain Score</div>
        </div>
      </div>

      {/* Prominent Start Lesson CTA */}
      {!hasCompleted && firstActive && (
        <button onClick={() => onNav("lesson", firstActive.id)} style={{
          width: "100%", background: group.color, color: "#fff", border: "none", borderRadius: 18,
          padding: "22px 24px", cursor: "pointer", fontSize: 22, fontWeight: 800,
          boxShadow: `0 6px 24px ${group.color}50`, marginBottom: 22,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
          letterSpacing: 0.3,
        }}>
          Start your first lesson →
        </button>
      )}

      <h3 style={{ fontSize: 14, fontWeight: 700, color: "#44403C", marginBottom: 12 }}>📚 Your Modules</h3>

      {/* Prominent Start Lesson button for active module */}
      {firstActive && hasCompleted && (
        <button onClick={() => onNav("lesson", firstActive.id)} style={{
          width: "100%", background: group.color, color: "#fff", border: "none", borderRadius: 16,
          padding: "18px 24px", cursor: "pointer", fontSize: 20, fontWeight: 800,
          boxShadow: `0 4px 18px ${group.color}50`, marginBottom: 12,
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
        }}>
          ▶ Start Lesson — {firstActive.title}
        </button>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {modules.map(mod => {
          const t = typeMap[mod.type] || typeMap.concept;
          return (
            <div key={mod.id} onClick={!mod.locked ? () => onNav("lesson", mod.id) : undefined} style={{
              background: mod.active ? `${group.color}12` : mod.complete ? "#ECFDF5" : mod.locked ? "#FAFAF9" : "#fff",
              border: mod.active ? `2px solid ${group.color}` : mod.complete ? "1px solid #10B98130" : "1px solid #E7E5E4",
              borderRadius: 12, padding: "12px 16px", cursor: mod.locked ? "default" : "pointer",
              opacity: mod.locked ? 0.5 : 1, transition: "all 0.15s"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 16 }}>{mod.locked ? "🔒" : mod.complete ? "✅" : mod.active ? "▶️" : "⭕"}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#44403C" }}>{mod.title}</div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 2, flexWrap: "wrap" }}>
                      <span style={{ background: t.bg, color: t.color, borderRadius: 99, padding: "1px 8px", fontSize: 10, fontWeight: 700 }}>{t.label}</span>
                      {mod.comingSoon && (
                        <span style={{ background: "#F5F3FF", color: "#7C3AED", borderRadius: 99, padding: "1px 8px", fontSize: 10, fontWeight: 700 }}>Coming soon</span>
                      )}
                    </div>
                  </div>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#D97706" }}>+{mod.xp} XP</span>
              </div>
              <BrainMeter score={mod.brainScore} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── AUDIO / SPEECH (Web Speech API) ───────────────────────────────────────────

function getMutedPref() {
  try { return localStorage.getItem("sproutcode_audio_muted") === "true"; } catch { return false; }
}

function setMutedPref(muted) {
  try { localStorage.setItem("sproutcode_audio_muted", String(muted)); } catch {}
}

function pickVoice() {
  try {
    const voices = window.speechSynthesis.getVoices() || [];
    if (!voices.length) return null;
    const bySamantha = voices.find(v => /samantha/i.test(v.name));
    if (bySamantha) return bySamantha;
    const byGoogleUkFemale = voices.find(v => /google uk english female/i.test(v.name));
    if (byGoogleUkFemale) return byGoogleUkFemale;
    const byZira = voices.find(v => /microsoft zira|\bzira\b/i.test(v.name));
    if (byZira) return byZira;
    const byFemale = voices.find(v => /female/i.test(v.name));
    if (byFemale) return byFemale;
    const anyEn = voices.find(v => /en/i.test(v.lang));
    return anyEn || null;
  } catch { return null; }
}

function speakText(text) {
  if (typeof window === "undefined" || !window.speechSynthesis) return;
  if (getMutedPref()) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.9;
    u.pitch = 1.2;
    u.volume = 1;
    const v = pickVoice();
    if (v) u.voice = v;
    window.speechSynthesis.speak(u);
  } catch {}
}

function advanceAfterSpeech(callback) {
  if (typeof window === "undefined" || !window.speechSynthesis) { callback(); return; }
  if (!window.speechSynthesis.speaking) { callback(); return; }
  const start = Date.now();
  const id = setInterval(() => {
    if (!window.speechSynthesis.speaking || Date.now() - start >= 4000) {
      clearInterval(id);
      callback();
    }
  }, 100);
}

function useSpeech(text, deps = []) {
  useEffect(() => {
    speakText(text);
    return () => { try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Prime voices list once (Chrome loads voices asynchronously).
if (typeof window !== "undefined" && window.speechSynthesis && typeof window.speechSynthesis.onvoiceschanged !== "undefined") {
  try { window.speechSynthesis.getVoices(); } catch {}
}

function MuteButton() {
  const [muted, setMuted] = useState(() => getMutedPref());
  const toggle = () => {
    const next = !muted;
    setMuted(next);
    setMutedPref(next);
    if (next) { try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch {} }
  };
  return (
    <button onClick={toggle} aria-label={muted ? "Unmute audio" : "Mute audio"} style={{
      position: "fixed", top: 72, right: 16, zIndex: 110,
      background: "#fff", border: "3px solid #FED7AA", borderRadius: 999,
      width: 54, height: 54, cursor: "pointer", fontSize: 26, padding: 0,
      boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
      display: "flex", alignItems: "center", justifyContent: "center",
      lineHeight: 1,
    }}>
      {muted ? "🔇" : "🔊"}
    </button>
  );
}

// ─── EXPLORERS LESSON (Ages 5–8) ───────────────────────────────────────────────

const EXPLORER_CARD_DISPLAY = {
  "Walk forward": { emoji: "🦶", word: "Walk" },
  "Turn right":   { emoji: "↩️", word: "Turn" },
  "Pick up star": { emoji: "⭐", word: "Grab" },
  "Stop":         { emoji: "🛑", word: "Stop" },
};

// Shared animation styles for Explorers lessons
function useExplorersAnimStyles() {
  useEffect(() => {
    if (document.getElementById("cs-explorers-shared-anim")) return;
    const el = document.createElement("style");
    el.id = "cs-explorers-shared-anim";
    el.textContent = `
      @keyframes csBounceEx { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
      @keyframes csFloatQ { 0%,100% { transform: translateY(0) rotate(-8deg); } 50% { transform: translateY(-10px) rotate(10deg); } }
      @keyframes csDanceEx {
        0%,100% { transform: rotate(-6deg); }
        25% { transform: rotate(10deg) translateY(-6px); }
        50% { transform: rotate(-10deg) translateY(-10px); }
        75% { transform: rotate(5deg); }
      }
      @keyframes csRobotWrongEx {
        0%   { transform: translate(0px, 0px); }
        45%  { transform: translate(170px, 0px); }
        55%  { transform: translate(150px, 0px); }
        65%  { transform: translate(160px, 0px); }
        100% { transform: translate(150px, 0px); }
      }
      @keyframes csWallShakeEx {
        0%, 38% { transform: translateX(0); }
        44% { transform: translateX(-7px); }
        52% { transform: translateX(7px); }
        58% { transform: translateX(-4px); }
        64%, 100% { transform: translateX(0); }
      }
      @keyframes csBoomEx {
        0%, 52% { opacity: 0; transform: translate(-50%, -50%) scale(0.3); }
        62%, 100% { opacity: 1; transform: translate(-50%, -50%) scale(1.15); }
      }
      @keyframes csRobotCorrectEx {
        0%   { transform: translate(0px, 0px); }
        42%  { transform: translate(130px, 0px); }
        72%  { transform: translate(130px, 50px); }
        100% { transform: translate(130px, 50px); }
      }
      @keyframes csStarGetEx {
        0%, 70%   { transform: scale(1); opacity: 0.35; filter: none; }
        78%, 100% { transform: scale(1.55); opacity: 1; filter: drop-shadow(0 0 14px #F59E0B); }
      }
      @keyframes csSparkleEx {
        0%, 72%  { opacity: 0; transform: scale(0.3); }
        82%, 100% { opacity: 1; transform: scale(1); }
      }
      @keyframes csConfettiEx {
        0%   { transform: translateY(0px) rotate(0deg); opacity: 1; }
        100% { transform: translateY(360px) rotate(540deg); opacity: 0; }
      }
      @keyframes csRainDropEx {
        0% { transform: translateY(-20px); opacity: 0; }
        30% { opacity: 1; }
        100% { transform: translateY(130px); opacity: 0; }
      }
      @keyframes csSunEx {
        0%,100% { transform: rotate(0deg) scale(1); }
        50% { transform: rotate(14deg) scale(1.08); }
      }
      @keyframes csStarGlowEx {
        0%,100% { filter: drop-shadow(0 0 0 transparent); transform: scale(1); }
        50% { filter: drop-shadow(0 0 14px #F59E0B); transform: scale(1.18); }
      }
      @keyframes csPopEx {
        0% { transform: scale(0.3); opacity: 0; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes csFloatUpEx {
        0% { transform: translate(-50%, 0); opacity: 1; }
        100% { transform: translate(-50%, -80px); opacity: 0; }
      }
      @keyframes csHeartUpEx {
        0% { transform: translateY(0) scale(1); opacity: 0.9; }
        100% { transform: translateY(-220px) scale(1.2); opacity: 0; }
      }
    `;
    document.head.appendChild(el);
    // Do not remove on unmount — keep for subsequent Explorers lessons.
  }, []);
}

const exSpeechBubbleStyle = {
  background: "#FFF7ED", border: "3px solid #FED7AA", borderRadius: 18,
  borderBottomLeftRadius: 6, padding: "12px 18px",
  fontSize: 20, fontWeight: 800, color: "#92400E", lineHeight: 1.4,
  maxWidth: 320, textAlign: "center", margin: "0 auto",
  boxShadow: "0 3px 10px rgba(249,115,22,0.12)",
};

function ExBack({ onClick }) {
  return (
    <button onClick={onClick} style={{
      background: "#FFF7ED", border: "2px solid #FED7AA", cursor: "pointer",
      color: "#92400E", fontSize: 16, fontWeight: 700, marginBottom: 18,
      borderRadius: 14, padding: "12px 20px", minHeight: 48,
    }}>← Back</button>
  );
}

function ExHeader({ number, title }) {
  return (
    <div style={{ background: "linear-gradient(135deg, #F97316, #EA580C)", borderRadius: 20, padding: "22px 26px", marginBottom: 20, color: "#fff" }}>
      <div style={{ fontSize: 12, opacity: 0.9, marginBottom: 4, letterSpacing: 0.5 }}>LESSON {number} · EXPLORERS</div>
      <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Georgia', serif" }}>{title}</div>
    </div>
  );
}

function ExplorersLesson1({ onNav, progress, setProgress }) {
  useExplorersAnimStyles();
  const [screen, setScreen] = useState("watch1"); // watch1, watch2, watch3, think, celebrate
  const [animStep, setAnimStep] = useState("wrong"); // wrong, transition, correct, done
  const [watchKey, setWatchKey] = useState(0);
  const [shuffledCards] = useState(() => [...EXPLORER_CORRECT_ORDER].sort(() => Math.random() - 0.5));
  const [slots, setSlots] = useState([null, null, null, null]);
  const [selected, setSelected] = useState(null);
  const [wrongSlots, setWrongSlots] = useState([]);
  const [checkResult, setCheckResult] = useState(null); // null | "wrong" | "right"
  const [confetti] = useState(() =>
    Array.from({ length: 14 }, () => ({
      emoji: ["🎉", "⭐", "🌟", "✨", "🎊", "🤖"][Math.floor(Math.random() * 6)],
      left: Math.random() * 88 + 4,
      delay: Math.random() * 1.6,
      duration: 1.9 + Math.random() * 1.2,
    }))
  );

  const allSlotsFilled = slots.every(s => s !== null);

  // Animation timing for watch2 — wrong → transition → correct → done
  useEffect(() => {
    if (screen !== "watch2") return;
    setAnimStep("wrong");
    const t1 = setTimeout(() => setAnimStep("transition"), 3500);
    const t2 = setTimeout(() => setAnimStep("correct"), 5000);
    const t3 = setTimeout(() => setAnimStep("done"), 8800);
    return () => [t1, t2, t3].forEach(clearTimeout);
  }, [screen, watchKey]);

  const narrations = {
    watch1: "Hi! I am Sparky your coding robot! I need your help to reach the star!",
    watch2: "I follow instructions one step at a time. Watch what happens when my instructions are wrong!",
    watch3: "Now it is your turn! Put the steps in the right order for me!",
    think: "Put my four steps in the right order! Tap a card, then tap a box!",
    celebrate: "Incredible! You wrote your very first algorithm! You are a programmer!",
  };
  useSpeech(narrations[screen], [screen]);

  const handleCardClick = (card) => {
    setSelected(sel => sel === card ? null : card);
  };

  const handleSlotClick = (idx) => {
    if (selected !== null) {
      const newSlots = [...slots];
      const displaced = newSlots[idx];
      newSlots[idx] = selected;
      setSlots(newSlots);
      setSelected(displaced);
    } else if (slots[idx] !== null) {
      const newSlots = [...slots];
      setSelected(newSlots[idx]);
      newSlots[idx] = null;
      setSlots(newSlots);
    }
    setWrongSlots(w => w.filter(i => i !== idx));
    if (checkResult === "wrong") setCheckResult(null);
  };

  const checkOrder = () => {
    const wrong = [];
    slots.forEach((s, i) => { if (s !== EXPLORER_CORRECT_ORDER[i]) wrong.push(i); });
    if (wrong.length === 0) {
      setCheckResult("right");
      advanceAfterSpeech(() => setScreen("celebrate"));
    } else {
      // Reset only wrong slots; keep correct ones
      const newSlots = slots.map((s, i) => wrong.includes(i) ? null : s);
      setSlots(newSlots);
      setWrongSlots(wrong);
      setSelected(null);
      setCheckResult("wrong");
      speakText("Hmm, the robot got a little lost! Try moving the steps around!");
    }
  };

  const handleComplete = () => {
    const updated = {
      ...progress,
      xp: progress.xp + 50,
      completedModules: [...new Set([...progress.completedModules, "little-1", "giving-instructions"])],
    };
    setProgress(updated);
    saveProgress(updated);
    advanceAfterSpeech(() => onNav("dashboard"));
  };

  const stageBase = {
    position: "relative", width: "100%", maxWidth: 340, height: 180,
    background: "linear-gradient(180deg, #BAE6FD 0%, #E0F2FE 65%, #86EFAC 100%)",
    borderRadius: 16, overflow: "hidden", margin: "0 auto", border: "2px solid #93C5FD",
  };

  const screenOrder = ["watch1", "watch2", "watch3", "think", "celebrate"];
  const handleBack = () => {
    const idx = screenOrder.indexOf(screen);
    if (idx <= 0) { onNav("dashboard"); return; }
    const prev = screenOrder[idx - 1];
    setCheckResult(null);
    setWrongSlots([]);
    if (prev === "watch2") setWatchKey(k => k + 1);
    setScreen(prev);
  };

  return (
    <div style={{ maxWidth: 660, margin: "0 auto", padding: "24px 20px", position: "relative" }}>
      <MuteButton />
      <ExBack onClick={handleBack} />
      <ExHeader number={1} title="Giving Instructions" />

      {/* WATCH 1 — Intro */}
      {screen === "watch1" && (
        <div style={{ background: "#fff", borderRadius: 24, padding: "30px 20px", border: "2px solid #FED7AA", textAlign: "center" }}>
          <div style={{ position: "relative", height: 230, marginBottom: 22 }}>
            <span style={{ position: "absolute", left: "50%", top: 30, transform: "translateX(-50%)", fontSize: 128, animation: "csBounceEx 1.8s ease-in-out infinite", lineHeight: 1 }}>🤖</span>
            <span style={{ position: "absolute", right: 4, top: 0, fontSize: 68, animation: "csStarGlowEx 1.6s ease-in-out infinite", lineHeight: 1 }}>⭐</span>
            <div style={{ position: "absolute", left: "54%", top: 60, color: "#F97316", fontSize: 36, letterSpacing: 10, opacity: 0.55 }}>· · ·</div>
          </div>
          <div style={exSpeechBubbleStyle}>Help me reach the star! ⭐</div>
          <button onClick={() => advanceAfterSpeech(() => setScreen("watch2"))} style={{
            marginTop: 28, background: "#F97316", color: "#fff", border: "none", borderRadius: 20,
            padding: "22px 44px", cursor: "pointer", fontSize: 26, fontWeight: 900,
            boxShadow: "0 6px 24px #F9731650", minHeight: 72,
          }}>Let's go! 🚀</button>
        </div>
      )}

      {/* WATCH 2 — Concept animation */}
      {screen === "watch2" && (
        <div style={{ background: "#fff", borderRadius: 24, padding: "24px 20px", border: "2px solid #FED7AA", textAlign: "center" }}>
          <div style={{ marginBottom: 18 }}>
            <div style={exSpeechBubbleStyle}>
              {animStep === "wrong"      ? "Wrong way! 💥" :
               animStep === "transition" ? "Now the right way! 🤖" :
               animStep === "correct"    ? "Right way! ⭐" :
                                          "Did you see that? 👀"}
            </div>
          </div>

          <div key={animStep + "-" + watchKey} style={{ ...stageBase, marginBottom: 22 }}>
            {animStep === "wrong" && (
              <>
                <div style={{
                  position: "absolute", left: 212, top: 14, width: 22, height: 130,
                  display: "flex", flexDirection: "column", alignItems: "center",
                  justifyContent: "space-around", animation: "csWallShakeEx 3.5s ease-in-out",
                }}>
                  {["🧱","🧱","🧱","🧱","🧱"].map((b, i) => <span key={i} style={{ fontSize: 18, lineHeight: 1 }}>{b}</span>)}
                </div>
                <div style={{
                  position: "absolute", left: "56%", top: "50%", fontSize: 62, lineHeight: 1,
                  animation: "csBoomEx 3.5s ease-in-out forwards",
                  pointerEvents: "none", zIndex: 10,
                }}>💥</div>
                <span style={{
                  position: "absolute", right: 22, top: 100, fontSize: 38, lineHeight: 1,
                  animation: "csBoomEx 3.5s ease-in-out forwards",
                  pointerEvents: "none", zIndex: 11,
                }}>😵</span>
              </>
            )}
            {(animStep === "correct" || animStep === "transition") && (
              <>
                <div style={{ position: "absolute", left: 46, top: 68, fontSize: 18, color: "#0EA5E9", letterSpacing: 6 }}>→→→</div>
                <div style={{ position: "absolute", left: 144, top: 76, fontSize: 18, color: "#0EA5E9" }}>↓</div>
              </>
            )}
            {animStep === "correct" && (
              <>
                <span style={{
                  position: "absolute", right: 12, top: 12, fontSize: 46, lineHeight: 1,
                  animation: "csSparkleEx 3.8s ease-in-out forwards",
                  pointerEvents: "none", zIndex: 10,
                }}>✅</span>
                <span style={{
                  position: "absolute", left: 22, top: 18, fontSize: 30, lineHeight: 1,
                  animation: "csSparkleEx 3.8s ease-in-out forwards",
                  pointerEvents: "none", zIndex: 10,
                }}>✨</span>
              </>
            )}
            <span style={{
              position: "absolute", left: 132, top: 104, fontSize: 36, display: "inline-block",
              opacity: animStep === "wrong" ? 0.25 : undefined,
              animation: animStep === "correct" ? "csStarGetEx 3.8s ease-in-out forwards" : "none",
            }}>⭐</span>
            <span style={{
              position: "absolute", left: 14, top: 52, fontSize: 36, display: "inline-block",
              animation: animStep === "wrong"
                ? "csRobotWrongEx 3.5s ease-in-out forwards"
                : animStep === "correct"
                ? "csRobotCorrectEx 3.8s ease-in-out forwards"
                : "none",
            }}>🤖</span>
          </div>

          {animStep === "done" && (
            <>
              <div style={{
                background: "#FFFBEB", border: "3px solid #FCD34D", borderRadius: 18,
                padding: "18px 14px", margin: "0 auto 20px", maxWidth: 360,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                boxShadow: "0 4px 14px rgba(245,158,11,0.15)",
              }} aria-label="Route map">
                <span style={{ fontSize: 40, lineHeight: 1 }}>🤖</span>
                <span style={{ fontSize: 34, color: "#0EA5E9", fontWeight: 900, lineHeight: 1 }}>→</span>
                <span style={{ fontSize: 34, color: "#0EA5E9", fontWeight: 900, lineHeight: 1 }}>→</span>
                <span style={{ fontSize: 34, color: "#0EA5E9", fontWeight: 900, lineHeight: 1 }}>↓</span>
                <span style={{ fontSize: 40, lineHeight: 1, filter: "drop-shadow(0 0 8px #F59E0B)" }}>⭐</span>
              </div>
              <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
                <button onClick={() => setWatchKey(k => k + 1)} style={{
                  background: "#FFF7ED", color: "#F97316", border: "3px solid #FED7AA",
                  borderRadius: 16, padding: "14px 22px", cursor: "pointer", fontSize: 18, fontWeight: 800,
                  minHeight: 64,
                }}>Watch again 🔄</button>
                <button onClick={() => advanceAfterSpeech(() => setScreen("watch3"))} style={{
                  background: "#F97316", color: "#fff", border: "none", borderRadius: 16,
                  padding: "14px 26px", cursor: "pointer", fontSize: 20, fontWeight: 900,
                  boxShadow: "0 4px 18px #F9731650", minHeight: 64,
                }}>I understand! →</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* WATCH 3 — Bridge */}
      {screen === "watch3" && (
        <div style={{ background: "#fff", borderRadius: 24, padding: "30px 20px", border: "2px solid #FED7AA", textAlign: "center" }}>
          <div style={{ position: "relative", height: 220, marginBottom: 22 }}>
            <span style={{ position: "absolute", left: "50%", top: 30, transform: "translateX(-50%)", fontSize: 128, animation: "csBounceEx 1.8s ease-in-out infinite", lineHeight: 1 }}>🤖</span>
            <span style={{ position: "absolute", left: "18%", top: 10, fontSize: 34, animation: "csFloatQ 2s ease-in-out infinite" }}>❓</span>
            <span style={{ position: "absolute", right: "18%", top: 34, fontSize: 38, animation: "csFloatQ 2.3s ease-in-out infinite 0.3s" }}>❓</span>
            <span style={{ position: "absolute", left: "14%", top: 130, fontSize: 28, animation: "csFloatQ 2.5s ease-in-out infinite 0.6s" }}>❓</span>
          </div>
          <div style={exSpeechBubbleStyle}>Your turn now! 🎯</div>
          <button onClick={() => advanceAfterSpeech(() => setScreen("think"))} style={{
            marginTop: 28, background: "#F97316", color: "#fff", border: "none", borderRadius: 20,
            padding: "22px 42px", cursor: "pointer", fontSize: 24, fontWeight: 900,
            boxShadow: "0 6px 22px #F9731650", minHeight: 72,
          }}>I'll help you! 🤖</button>
        </div>
      )}

      {/* THINK + CHECK */}
      {screen === "think" && (
        <div style={{ background: "#fff", borderRadius: 24, padding: "24px 18px", border: "2px solid #FED7AA" }}>
          {/* Persistent reference map — same style as WATCH phase done state */}
          <div style={{ textAlign: "center", marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: "#A8A29E", letterSpacing: 1, marginBottom: 6 }}>GOAL</div>
            <div style={{
              background: "#FFFBEB", border: "3px solid #FCD34D", borderRadius: 16,
              padding: "10px 12px", margin: "0 auto", maxWidth: 300,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              boxShadow: "0 2px 8px rgba(245,158,11,0.12)",
            }} aria-label="Route map">
              <span style={{ fontSize: 30, lineHeight: 1 }}>🤖</span>
              <span style={{ fontSize: 26, color: "#0EA5E9", fontWeight: 900, lineHeight: 1 }}>→</span>
              <span style={{ fontSize: 26, color: "#0EA5E9", fontWeight: 900, lineHeight: 1 }}>→</span>
              <span style={{ fontSize: 26, color: "#0EA5E9", fontWeight: 900, lineHeight: 1 }}>↓</span>
              <span style={{ fontSize: 30, lineHeight: 1, filter: "drop-shadow(0 0 6px #F59E0B)" }}>⭐</span>
            </div>
          </div>

          <div style={{ textAlign: "center", marginBottom: 18 }}>
            <div style={{ fontSize: 72, marginBottom: 10, lineHeight: 1 }}>🤖</div>
            <div style={exSpeechBubbleStyle}>Put me in order! 📋</div>
          </div>

          {checkResult === "wrong" && (
            <div style={{ background: "#FEF3C7", borderRadius: 16, padding: "16px", marginBottom: 18, border: "3px solid #F59E0B", textAlign: "center" }}>
              <div style={{ fontSize: 56, lineHeight: 1 }}>😅</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#92400E", marginTop: 6 }}>Try again! 🔄</div>
            </div>
          )}

          {/* Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 18 }}>
            {shuffledCards.map(card => {
              const isPlaced = slots.includes(card);
              const isSelected = selected === card;
              const d = EXPLORER_CARD_DISPLAY[card];
              return (
                <button key={card} onClick={() => !isPlaced && handleCardClick(card)} disabled={isPlaced} style={{
                  background: isSelected ? "#F97316" : isPlaced ? "#F5F5F4" : "#FFF7ED",
                  color: isSelected ? "#fff" : isPlaced ? "#A8A29E" : "#44403C",
                  border: `3px solid ${isSelected ? "#EA580C" : isPlaced ? "#E7E5E4" : "#FED7AA"}`,
                  borderRadius: 18, padding: "14px 8px", cursor: isPlaced ? "default" : "pointer",
                  fontSize: 18, fontWeight: 900, transition: "all 0.15s",
                  transform: isSelected ? "scale(1.08)" : "scale(1)",
                  boxShadow: isSelected ? "0 6px 20px #F9731650" : "none",
                  opacity: isPlaced ? 0.3 : 1, minHeight: 96,
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
                }}>
                  <span style={{ fontSize: 36, lineHeight: 1 }}>{d.emoji}</span>
                  <span>{d.word}</span>
                </button>
              );
            })}
          </div>

          {/* Slots */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
            {slots.map((slot, i) => {
              const isWrong = wrongSlots.includes(i);
              const d = slot ? EXPLORER_CARD_DISPLAY[slot] : null;
              return (
                <button key={i} onClick={() => handleSlotClick(i)} style={{
                  width: "100%",
                  background: isWrong ? "#FEF3C7" : slot ? "#FFF7ED" : selected ? "#FFFBEB" : "#FAFAF9",
                  border: `3px ${isWrong ? "solid #F59E0B" : slot ? "solid #F97316" : selected ? "dashed #F97316" : "dashed #D6D3D1"}`,
                  borderRadius: 14, padding: "14px 18px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 16, transition: "all 0.15s",
                  minHeight: 72,
                  boxShadow: isWrong ? "0 0 0 4px #F59E0B40" : "none",
                }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
                    background: slot ? "#F97316" : "#E7E5E4", color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 900, fontSize: 22,
                  }}>{i + 1}</div>
                  <div style={{ flex: 1, textAlign: "left", display: "flex", alignItems: "center", gap: 12 }}>
                    {d ? (
                      <>
                        <span style={{ fontSize: 40, lineHeight: 1 }}>{d.emoji}</span>
                        <span style={{ fontSize: 20, fontWeight: 900, color: "#44403C" }}>{d.word}</span>
                      </>
                    ) : (
                      <span style={{ fontSize: 16, color: "#A8A29E", fontWeight: 700 }}>{selected ? "← tap to place" : "empty"}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {allSlotsFilled && (
            <button onClick={checkOrder} style={{
              width: "100%", background: "#F97316", color: "#fff", border: "none",
              borderRadius: 18, padding: "20px", cursor: "pointer", fontSize: 24, fontWeight: 900,
              boxShadow: "0 6px 24px #F9731650", animation: "csBounceEx 1.2s ease-in-out infinite",
              minHeight: 80,
            }}>Check it! 🤖</button>
          )}
        </div>
      )}

      {/* CELEBRATE */}
      {screen === "celebrate" && (
        <div style={{ background: "#fff", borderRadius: 24, padding: "32px 22px", border: "2px solid #FED7AA", textAlign: "center", position: "relative", overflow: "hidden" }}>
          {confetti.map((c, i) => (
            <span key={i} style={{
              position: "absolute", left: `${c.left}%`, top: -24, fontSize: 26, zIndex: 10,
              animation: `csConfettiEx ${c.duration}s ${c.delay}s ease-in infinite`,
              pointerEvents: "none",
            }}>{c.emoji}</span>
          ))}
          <div style={{ fontSize: 140, animation: "csDanceEx 1.2s ease-in-out infinite", marginBottom: 14, lineHeight: 1 }}>🕺🤖</div>
          <div style={{ fontSize: 40, letterSpacing: 10, marginBottom: 12 }}>⭐✨⭐✨⭐</div>
          <div style={{ fontSize: 32, fontWeight: 900, color: "#44403C", fontFamily: "'Georgia', serif", marginBottom: 14 }}>You did it! ⭐</div>
          <div style={exSpeechBubbleStyle}>You're a programmer! 🎉</div>
          <div style={{ marginTop: 22, display: "inline-block", background: "linear-gradient(135deg, #FEF3C7, #FFF7ED)", borderRadius: 14, padding: "12px 22px", border: "2px solid #F59E0B" }}>
            <span style={{ fontSize: 22, fontWeight: 900, color: "#D97706" }}>+50 XP</span>
          </div>
          <div style={{ marginTop: 28 }}>
            <button onClick={handleComplete} style={{
              background: "#F97316", color: "#fff", border: "none", borderRadius: 18,
              padding: "20px 40px", cursor: "pointer", fontSize: 22, fontWeight: 900,
              boxShadow: "0 6px 22px #F9731650", minHeight: 72,
            }}>Back to lessons →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── EXPLORERS LESSON 2 — Yes or No Decisions ──────────────────────────────────

function ExplorersLesson2({ onNav, progress, setProgress }) {
  useExplorersAnimStyles();
  const [screen, setScreen] = useState("watch1"); // watch1, watch2, watch3, think, celebrate
  const [scenarioIdx, setScenarioIdx] = useState(0);
  const [answered, setAnswered] = useState([false, false, false]);
  const [feedback, setFeedback] = useState(null); // null | "right" | "wrong"
  const [watchKey, setWatchKey] = useState(0);
  const [animStep, setAnimStep] = useState("approach"); // approach, look, decide, move, done
  const [hearts] = useState(() =>
    Array.from({ length: 12 }, (_, i) => ({
      emoji: ["✨", "💚", "⭐", "✨", "💚"][i % 5],
      left: Math.random() * 88 + 4,
      delay: Math.random() * 1.6,
      duration: 1.8 + Math.random() * 1.2,
    }))
  );

  const scenarios = [
    { kind: "rain", question: "Take umbrella? 🤔", audio: "It is raining! Should the robot take an umbrella?",        correct: true,  rightAudio: "That's right! An umbrella is perfect for the rain!" },
    { kind: "sun",  question: "Wear raincoat? 🤔", audio: "It is sunny! Should the robot wear a raincoat?",            correct: false, rightAudio: "That's right! No raincoat needed on a sunny day!" },
    { kind: "fork", question: "Go left? 🤔",       audio: "There is a star on the left! Should the robot go left?",   correct: true,  rightAudio: "That's right! The star is on the left!" },
  ];

  const staticNarrations = {
    watch1: "Computers make decisions! They ask yes or no questions!",
    watch2: "Watch! The robot sees a star and makes a decision!",
    watch3: "Now you help the robot decide! Yes or No?",
    celebrate: "Wow! You think just like a computer! Amazing job!",
  };

  // Narration per screen / scenario
  useEffect(() => {
    if (screen === "think") {
      const prefix = scenarioIdx === 0 ? "Help the robot! Tap YES or NO! " : "";
      speakText(prefix + scenarios[scenarioIdx].audio);
    } else if (staticNarrations[screen]) {
      speakText(staticNarrations[screen]);
    }
    return () => { try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, scenarioIdx]);

  // Animation timing for watch2
  useEffect(() => {
    if (screen !== "watch2") return;
    setAnimStep("approach");
    const t1 = setTimeout(() => setAnimStep("look"),   1800);
    const t2 = setTimeout(() => setAnimStep("decide"), 3100);
    const t3 = setTimeout(() => setAnimStep("move"),   4500);
    const t4 = setTimeout(() => setAnimStep("done"),   6700);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, [screen, watchKey]);

  const handleAnswer = (value) => {
    if (feedback === "right") return;
    const correct = value === scenarios[scenarioIdx].correct;
    if (correct) {
      setFeedback("right");
      speakText(scenarios[scenarioIdx].rightAudio);
      setAnswered(a => {
        const na = [...a]; na[scenarioIdx] = true; return na;
      });
      // Give the just-queued utterance a moment to register as "speaking",
      // then wait for it to finish before advancing.
      setTimeout(() => {
        advanceAfterSpeech(() => {
          if (scenarioIdx >= scenarios.length - 1) {
            setScreen("celebrate");
          } else {
            setScenarioIdx(i => i + 1);
            setFeedback(null);
          }
        });
      }, 150);
    } else {
      setFeedback("wrong");
      speakText("Hmm, look again! What do you think?");
    }
  };

  const retryScenario = () => {
    setFeedback(null);
    speakText(scenarios[scenarioIdx].audio);
  };

  const screenOrder = ["watch1", "watch2", "watch3", "think", "celebrate"];
  const handleBack = () => {
    const idx = screenOrder.indexOf(screen);
    if (idx <= 0) { onNav("dashboard"); return; }
    const prev = screenOrder[idx - 1];
    setFeedback(null);
    if (prev === "watch2") setWatchKey(k => k + 1);
    setScreen(prev);
  };

  const handleComplete = () => {
    const updated = {
      ...progress,
      xp: progress.xp + 60,
      completedModules: [...new Set([...progress.completedModules, "little-2", "yes-no-decisions"])],
    };
    setProgress(updated);
    saveProgress(updated);
    advanceAfterSpeech(() => onNav("dashboard"));
  };

  const sceneBase = {
    position: "relative", width: "100%", maxWidth: 340, height: 200,
    margin: "0 auto", background: "linear-gradient(180deg, #BAE6FD 0%, #E0F2FE 60%, #86EFAC 100%)",
    borderRadius: 16, border: "2px solid #93C5FD", overflow: "hidden",
  };

  return (
    <div style={{ maxWidth: 660, margin: "0 auto", padding: "24px 20px", position: "relative" }}>
      <MuteButton />
      <ExBack onClick={handleBack} />
      <ExHeader number={2} title="Yes or No Decisions" />

      {/* WATCH 1 — Intro */}
      {screen === "watch1" && (
        <div style={{ background: "#fff", borderRadius: 24, padding: "26px 20px", border: "2px solid #FED7AA", textAlign: "center" }}>
          <div style={{ position: "relative", height: 190, marginBottom: 14 }}>
            <span style={{ position: "absolute", left: "50%", top: 10, transform: "translateX(-50%)", fontSize: 112, animation: "csBounceEx 1.8s ease-in-out infinite", lineHeight: 1 }}>🤖</span>
            <div style={{ position: "absolute", left: "8%", top: 118, fontSize: 44, background: "#DCFCE7", borderRadius: 16, padding: "6px 14px", border: "3px solid #16A34A" }}>✅</div>
            <div style={{ position: "absolute", right: "8%", top: 118, fontSize: 44, background: "#FEF3C7", borderRadius: 16, padding: "6px 14px", border: "3px solid #F59E0B" }}>❌</div>
          </div>
          <div style={exSpeechBubbleStyle}>Yes or No? 🤔</div>

          <div style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, background: "#ECFDF5", borderRadius: 18, padding: "14px", border: "3px solid #86EFAC" }}>
              <span style={{ fontSize: 48 }}>🌧️</span>
              <span style={{ fontSize: 32, color: "#065F46" }}>→</span>
              <span style={{ fontSize: 48 }}>☂️</span>
              <span style={{ fontSize: 44 }}>✅</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, background: "#FEF3C7", borderRadius: 18, padding: "14px", border: "3px solid #FCD34D" }}>
              <span style={{ fontSize: 48 }}>☀️</span>
              <span style={{ fontSize: 32, color: "#92400E" }}>→</span>
              <span style={{ fontSize: 48 }}>☂️</span>
              <span style={{ fontSize: 44 }}>❌</span>
            </div>
          </div>

          <button onClick={() => advanceAfterSpeech(() => setScreen("watch2"))} style={{
            marginTop: 26, background: "#F97316", color: "#fff", border: "none", borderRadius: 20,
            padding: "20px 44px", cursor: "pointer", fontSize: 24, fontWeight: 900,
            boxShadow: "0 6px 24px #F9731650", minHeight: 72,
          }}>I see it! →</button>
        </div>
      )}

      {/* WATCH 2 — Decision tree animation */}
      {screen === "watch2" && (
        <div style={{ background: "#fff", borderRadius: 24, padding: "24px 20px", border: "2px solid #FED7AA", textAlign: "center" }}>
          <div style={exSpeechBubbleStyle}>
            {animStep === "approach" ? "Which way? 🤔" :
             animStep === "look"     ? "I see a star! ⭐" :
             animStep === "decide"   ? "Yes! Go left! ✅" :
             animStep === "move"     ? "Following the path... 🤖" :
                                      "Star here! Go left! ✅"}
          </div>

          <div key={watchKey + "-" + animStep} style={{ ...sceneBase, marginTop: 18 }}>
            {/* Path */}
            <div style={{ position: "absolute", left: "50%", bottom: 0, width: 18, height: 90, background: "#A16207", transform: "translateX(-50%)" }} />
            <div style={{ position: "absolute", left: "12%", bottom: 52, width: 110, height: 14, background: "#A16207", transform: "rotate(22deg)", transformOrigin: "right center" }} />
            <div style={{ position: "absolute", right: "12%", bottom: 52, width: 110, height: 14, background: "#A16207", transform: "rotate(-22deg)", transformOrigin: "left center" }} />

            {/* Star left (glowing) */}
            <span style={{ position: "absolute", left: 26, top: 30, fontSize: 46, animation: "csStarGlowEx 1.6s ease-in-out infinite", lineHeight: 1 }}>⭐</span>

            {/* Wall right */}
            <span style={{ position: "absolute", right: 26, top: 36, fontSize: 42, opacity: animStep === "approach" ? 0.7 : 1, lineHeight: 1 }}>🧱</span>

            {/* Thinking question mark */}
            {animStep === "look" && (
              <span style={{ position: "absolute", left: "50%", top: 70, transform: "translateX(-50%)", fontSize: 52, animation: "csFloatUpEx 1.3s ease-out forwards", lineHeight: 1 }}>❓</span>
            )}

            {/* Decision check ✅ over star */}
            {(animStep === "decide" || animStep === "move" || animStep === "done") && (
              <span style={{ position: "absolute", left: 70, top: 20, fontSize: 40, animation: "csPopEx 0.5s ease-out", lineHeight: 1 }}>✅</span>
            )}

            {/* Robot */}
            <span style={{
              position: "absolute", fontSize: 46, lineHeight: 1,
              transition: "all 1.3s ease-in-out",
              left: (animStep === "move" || animStep === "done") ? "22%" : "50%",
              bottom: (animStep === "move" || animStep === "done") ? 76 : 8,
              transform: "translateX(-50%)",
            }}>🤖</span>

            {/* Sparkles when done */}
            {animStep === "done" && (
              <>
                <span style={{ position: "absolute", left: 16, top: 14, fontSize: 26, animation: "csBounceEx 0.8s ease-in-out infinite", lineHeight: 1 }}>✨</span>
                <span style={{ position: "absolute", left: 56, top: 62, fontSize: 22, animation: "csBounceEx 0.9s ease-in-out infinite 0.2s", lineHeight: 1 }}>✨</span>
                <span style={{ position: "absolute", left: 90, top: 24, fontSize: 24, animation: "csBounceEx 1.0s ease-in-out infinite 0.4s", lineHeight: 1 }}>✨</span>
              </>
            )}
          </div>

          {animStep === "done" && (
            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginTop: 22 }}>
              <button onClick={() => setWatchKey(k => k + 1)} style={{
                background: "#FFF7ED", color: "#F97316", border: "3px solid #FED7AA",
                borderRadius: 16, padding: "14px 22px", cursor: "pointer", fontSize: 18, fontWeight: 800,
                minHeight: 64,
              }}>Watch again 🔄</button>
              <button onClick={() => advanceAfterSpeech(() => setScreen("watch3"))} style={{
                background: "#F97316", color: "#fff", border: "none", borderRadius: 16,
                padding: "14px 26px", cursor: "pointer", fontSize: 20, fontWeight: 900,
                boxShadow: "0 4px 18px #F9731650", minHeight: 64,
              }}>My turn! →</button>
            </div>
          )}
        </div>
      )}

      {/* WATCH 3 — Bridge */}
      {screen === "watch3" && (
        <div style={{ background: "#fff", borderRadius: 24, padding: "32px 20px", border: "2px solid #FED7AA", textAlign: "center" }}>
          <div style={{ fontSize: 140, animation: "csBounceEx 1.8s ease-in-out infinite", marginBottom: 14, lineHeight: 1 }}>🤖</div>
          <div style={exSpeechBubbleStyle}>Help me decide! 🤔</div>
          <button onClick={() => advanceAfterSpeech(() => { setScreen("think"); setScenarioIdx(0); setFeedback(null); setAnswered([false, false, false]); })} style={{
            marginTop: 28, background: "#F97316", color: "#fff", border: "none", borderRadius: 20,
            padding: "22px 44px", cursor: "pointer", fontSize: 24, fontWeight: 900,
            boxShadow: "0 6px 22px #F9731650", minHeight: 72,
          }}>Let's decide! →</button>
        </div>
      )}

      {/* THINK + CHECK */}
      {screen === "think" && (
        <div style={{ background: "#fff", borderRadius: 24, padding: "22px 18px", border: "2px solid #FED7AA" }}>
          {/* Progress dots */}
          <div style={{ display: "flex", justifyContent: "center", gap: 14, marginBottom: 18 }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 26, height: 26, borderRadius: "50%",
                background: answered[i] ? "#10B981" : i === scenarioIdx ? "#F97316" : "#E7E5E4",
                color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 15, fontWeight: 900,
                boxShadow: i === scenarioIdx && !answered[i] ? "0 0 0 4px #F9731633" : "none",
                transition: "all 0.2s",
              }}>{answered[i] ? "✓" : ""}</div>
            ))}
          </div>

          {/* Scene */}
          <div style={{ ...sceneBase, marginBottom: 16 }}>
            {scenarios[scenarioIdx].kind === "rain" && (
              <>
                {[0,1,2,3,4,5,6,7].map(i => (
                  <span key={i} style={{ position: "absolute", left: `${8 + i*12}%`, top: -18, fontSize: 24, animation: `csRainDropEx 1.3s linear infinite ${i*0.15}s`, lineHeight: 1 }}>💧</span>
                ))}
                <span style={{ position: "absolute", left: 36, bottom: 20, fontSize: 60, lineHeight: 1 }}>🤖</span>
                <span style={{ position: "absolute", right: 38, bottom: 28, fontSize: 60, lineHeight: 1 }}>☂️</span>
              </>
            )}
            {scenarios[scenarioIdx].kind === "sun" && (
              <>
                <span style={{ position: "absolute", left: "50%", top: 14, transform: "translateX(-50%)", fontSize: 68, animation: "csSunEx 2s ease-in-out infinite", lineHeight: 1 }}>☀️</span>
                <span style={{ position: "absolute", left: 44, bottom: 20, fontSize: 58, lineHeight: 1 }}>🤖</span>
                <span style={{ position: "absolute", right: 40, bottom: 24, fontSize: 58, lineHeight: 1 }}>🧥</span>
              </>
            )}
            {scenarios[scenarioIdx].kind === "fork" && (
              <>
                <div style={{ position: "absolute", left: "50%", bottom: 0, width: 18, height: 90, background: "#A16207", transform: "translateX(-50%)" }} />
                <div style={{ position: "absolute", left: "12%", bottom: 52, width: 110, height: 14, background: "#A16207", transform: "rotate(22deg)", transformOrigin: "right center" }} />
                <div style={{ position: "absolute", right: "12%", bottom: 52, width: 110, height: 14, background: "#A16207", transform: "rotate(-22deg)", transformOrigin: "left center" }} />
                <span style={{ position: "absolute", left: 22, top: 24, fontSize: 52, animation: "csStarGlowEx 1.4s ease-in-out infinite", lineHeight: 1 }}>⭐</span>
                <span style={{ position: "absolute", right: 26, top: 34, fontSize: 44, lineHeight: 1 }}>🧱</span>
                <span style={{ position: "absolute", left: "50%", bottom: 8, transform: "translateX(-50%)", fontSize: 50, lineHeight: 1 }}>🤖</span>
              </>
            )}
          </div>

          {/* Speech bubble */}
          <div style={{ textAlign: "center", marginBottom: 18 }}>
            <div style={exSpeechBubbleStyle}>{scenarios[scenarioIdx].question}</div>
          </div>

          {/* Feedback */}
          {feedback === "right" && (
            <div style={{ textAlign: "center", padding: "18px", background: "#DCFCE7", borderRadius: 18, border: "3px solid #10B981", marginBottom: 16 }}>
              <div style={{ fontSize: 90, animation: "csPopEx 0.4s ease-out", lineHeight: 1 }}>✅</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#065F46", marginTop: 8 }}>Great thinking!</div>
            </div>
          )}
          {feedback === "wrong" && (
            <div style={{ textAlign: "center", padding: "18px", background: "#FEF3C7", borderRadius: 18, border: "3px solid #F59E0B", marginBottom: 16 }}>
              <div style={{ fontSize: 80, lineHeight: 1 }}>🤔</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: "#92400E", marginBottom: 14, marginTop: 6 }}>Hmm, look again!</div>
              <button onClick={retryScenario} style={{
                background: "#F97316", color: "#fff", border: "none", borderRadius: 14,
                padding: "14px 30px", cursor: "pointer", fontSize: 18, fontWeight: 900,
                minHeight: 64,
              }}>Try again 🔄</button>
            </div>
          )}

          {/* YES / NO */}
          {feedback !== "right" && feedback !== "wrong" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <button onClick={() => handleAnswer(true)} style={{
                background: "#10B981", color: "#fff", border: "4px solid #059669",
                borderRadius: 20, padding: "18px", cursor: "pointer", fontSize: 26, fontWeight: 900,
                boxShadow: "0 6px 24px #10B98150", minHeight: 96,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
              }}>
                <span style={{ fontSize: 46, lineHeight: 1 }}>✅</span>
                <span>YES</span>
              </button>
              <button onClick={() => handleAnswer(false)} style={{
                background: "#F59E0B", color: "#fff", border: "4px solid #D97706",
                borderRadius: 20, padding: "18px", cursor: "pointer", fontSize: 26, fontWeight: 900,
                boxShadow: "0 6px 24px #F59E0B50", minHeight: 96,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
              }}>
                <span style={{ fontSize: 46, lineHeight: 1 }}>❌</span>
                <span>NO</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* CELEBRATE */}
      {screen === "celebrate" && (
        <div style={{ background: "#fff", borderRadius: 24, padding: "32px 22px", border: "2px solid #FED7AA", textAlign: "center", position: "relative", overflow: "hidden" }}>
          {hearts.map((h, i) => (
            <span key={i} style={{
              position: "absolute", left: `${h.left}%`, bottom: 0, fontSize: 26, zIndex: 10,
              animation: `csHeartUpEx ${h.duration}s ${h.delay}s ease-out infinite`,
              pointerEvents: "none",
            }}>{h.emoji}</span>
          ))}
          <div style={{ fontSize: 140, animation: "csDanceEx 1.2s ease-in-out infinite", marginBottom: 12, lineHeight: 1 }}>🤖</div>
          <div style={{ fontSize: 30, letterSpacing: 8, marginBottom: 4 }}>✨💚✨</div>
          <div style={{ fontSize: 30, letterSpacing: 8, marginBottom: 14 }}>💚✨💚</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: "#44403C", fontFamily: "'Georgia', serif", marginBottom: 14, lineHeight: 1.3 }}>
            You decide like a computer! 🧠
          </div>
          <div style={{ display: "inline-block", background: "linear-gradient(135deg, #FEF3C7, #FFF7ED)", borderRadius: 14, padding: "12px 22px", border: "2px solid #F59E0B" }}>
            <span style={{ fontSize: 22, fontWeight: 900, color: "#D97706" }}>+60 XP</span>
          </div>

          <div style={{ marginTop: 26, background: "#F5F3FF", borderRadius: 18, padding: "14px 18px", border: "3px dashed #C4B5FD", display: "flex", alignItems: "center", gap: 14, justifyContent: "center" }}>
            <span style={{ fontSize: 40, lineHeight: 1 }}>🔄</span>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: "#7C3AED", letterSpacing: 0.5 }}>NEXT LESSON</div>
              <div style={{ fontSize: 16, fontWeight: 900, color: "#44403C" }}>Making things repeat!</div>
            </div>
          </div>

          <div style={{ marginTop: 26 }}>
            <button onClick={handleComplete} style={{
              background: "#F97316", color: "#fff", border: "none", borderRadius: 18,
              padding: "20px 40px", cursor: "pointer", fontSize: 22, fontWeight: 900,
              boxShadow: "0 6px 22px #F9731650", minHeight: 72,
            }}>Back to lessons →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PYODIDE LOADER ────────────────────────────────────────────────────────────

let _pyodide = null;
let _pyodideLoading = null;

function getPyodide() {
  if (_pyodide) return Promise.resolve(_pyodide);
  if (_pyodideLoading) return _pyodideLoading;
  _pyodideLoading = (async () => {
    if (!window.loadPyodide) {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/pyodide/v0.23.4/full/pyodide.js";
        script.onload = resolve;
        script.onerror = () => reject(new Error("Could not load Python"));
        document.head.appendChild(script);
      });
    }
    _pyodide = await window.loadPyodide();
    return _pyodide;
  })();
  _pyodideLoading.catch(() => { _pyodideLoading = null; });
  return _pyodideLoading;
}

// ─── BUILDERS LESSON (Ages 9–12) ───────────────────────────────────────────────

function BuildersLesson1({ onNav, progress, setProgress }) {
  const color = "#0D9488";
  const [lessonPhase, setLessonPhase] = useState("watch"); // watch | think | check | celebrate

  // Watch phase
  const [watchExplained, setWatchExplained] = useState(false);
  const [watchStep, setWatchStep] = useState(-1); // -1=waiting, 0-4=running, 5=done
  const [hellos, setHellos] = useState([]);
  const [watchKey, setWatchKey] = useState(0);
  const watchIntervalRef = useRef(null);

  // Think/Check phase
  const [code, setCode] = useState("");
  const [runStatus, setRunStatus] = useState(null); // null | "loading" | "running"
  const [actualOutput, setActualOutput] = useState(null);
  const [failMsg, setFailMsg] = useState(null);
  const [pyError, setPyError] = useState(null);
  const [pyErrorType, setPyErrorType] = useState(null); // null | "indent" | "syntax" | "other"

  // Inject animations
  useEffect(() => {
    const el = document.createElement("style");
    el.id = "cs-builders-anim";
    el.textContent = `
      @keyframes csBuildersCount {
        0%   { transform: scale(1.18); color: #fff; box-shadow: 0 0 14px #0D9488; }
        100% { transform: scale(1);    color: #5EEAD4; box-shadow: none; }
      }
      @keyframes csHelloIn {
        0%   { opacity: 0; transform: translateX(-10px); }
        100% { opacity: 1; transform: translateX(0); }
      }
    `;
    document.head.appendChild(el);
    return () => { const old = document.getElementById("cs-builders-anim"); if (old) old.remove(); };
  }, []);

  // Watch animation driver — only starts after user reads explanation
  useEffect(() => {
    if (lessonPhase !== "watch" || !watchExplained) return;
    setWatchStep(-1);
    setHellos([]);
    const startTimer = setTimeout(() => {
      setWatchStep(0);
      setHellos(["Hello!"]);
      let i = 1;
      watchIntervalRef.current = setInterval(() => {
        if (i <= 4) {
          setWatchStep(i);
          setHellos(h => [...h, "Hello!"]);
          i++;
        } else {
          clearInterval(watchIntervalRef.current);
          setWatchStep(5);
        }
      }, 600);
    }, 400);
    return () => { clearTimeout(startTimer); clearInterval(watchIntervalRef.current); };
  }, [lessonPhase, watchKey, watchExplained]);

  const replayWatch = () => {
    clearInterval(watchIntervalRef.current);
    setWatchKey(k => k + 1);
  };

  const runCode = async () => {
    setFailMsg(null);
    setPyError(null);
    setPyErrorType(null);
    setActualOutput(null);
    setLessonPhase("check");
    setRunStatus("loading");
    let pyodide;
    try {
      pyodide = await getPyodide();
    } catch {
      setFailMsg("Couldn't load Python — check your connection and try again.");
      setRunStatus(null);
      setLessonPhase("think");
      return;
    }
    setRunStatus("running");
    try {
      pyodide.globals.set("_user_code", code);
      const output = await pyodide.runPythonAsync(`
import sys, io
_buf = io.StringIO()
sys.stdout = _buf
try:
    exec(compile(_user_code, '<student>', 'exec'))
finally:
    sys.stdout = sys.__stdout__
_buf.getvalue()
      `);
      const lines = output.split("\n").filter(l => l.trim() !== "");
      setActualOutput(lines);
      const isCorrect = lines.length === 5 && lines.every(l => l.toLowerCase().includes("hello"));
      if (isCorrect) {
        setLessonPhase("celebrate");
      } else {
        setFailMsg("Python ran but the output wasn't quite right. Check your print statement and make sure it prints exactly 5 times.");
        setLessonPhase("think");
      }
    } catch (err) {
      const fullMsg = err.message || "";
      const technical = fullMsg.split("\n").filter(l => l.trim()).slice(-2).join(" — ");
      let errorType = "other";
      if (fullMsg.includes("IndentationError")) errorType = "indent";
      else if (fullMsg.includes("SyntaxError")) errorType = "syntax";
      setPyErrorType(errorType);
      setPyError(technical);
      setLessonPhase("think");
    }
    setRunStatus(null);
  };

  const handleComplete = () => {
    const updated = {
      ...progress,
      xp: progress.xp + 80,
      completedModules: [...new Set([...progress.completedModules, "middle-1"])],
    };
    setProgress(updated);
    saveProgress(updated);
    onNav("dashboard");
  };

  const isRunning = runStatus !== null;

  const bPhaseSteps = ["watch", "think", "check", "celebrate"];
  const handleBack = () => {
    const idx = bPhaseSteps.indexOf(lessonPhase);
    if (idx <= 0) { onNav("dashboard"); return; }
    const prev = bPhaseSteps[idx - 1];
    if (prev === "watch") { setLessonPhase("watch"); setWatchExplained(false); setWatchStep(-1); setHellos([]); }
    else { setLessonPhase(prev); }
  };

  return (
    <div style={{ maxWidth: 660, margin: "0 auto", padding: "24px 20px" }}>
      <button onClick={handleBack} style={{ background: "none", border: "none", cursor: "pointer", color: "#78716C", fontSize: 14, fontWeight: 600, marginBottom: 18 }}>← Back</button>

      <div style={{ background: "linear-gradient(135deg, #0D9488, #0F766E)", borderRadius: 20, padding: "22px 26px", marginBottom: 20, color: "#fff" }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>MODULE 1 · FOR LOOPS</div>
        <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Georgia', serif" }}>For Loops</div>
      </div>

      <LessonProgress phase={lessonPhase} color={color} onStepClick={(step) => {
        if (step === "watch") {
          setLessonPhase("watch");
          setWatchExplained(false);
          setWatchStep(-1);
          setHellos([]);
        } else {
          setLessonPhase(step);
        }
      }} />

      {/* ── WATCH ── */}
      {lessonPhase === "watch" && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "20px", border: "1px solid #E7E5E4" }}>

          {/* ── EXPLAIN sub-phase ── */}
          {!watchExplained && (
            <>
              <div style={{ fontWeight: 800, color: "#44403C", fontSize: 16, marginBottom: 16 }}>
                Here's how a for loop is written in Python
              </div>

              {/* Syntax-coloured code block */}
              <pre style={{ background: "#1e1e1e", borderRadius: 10, padding: "16px 18px", marginBottom: 18, fontFamily: "'Courier New', monospace", fontSize: 14, lineHeight: 2.1, margin: "0 0 18px", overflowX: "auto" }}>
                <span style={{ color: "#569CD6" }}>for </span><span style={{ color: "#9CDCFE" }}>i </span><span style={{ color: "#569CD6" }}>in </span><span style={{ color: "#DCDCAA" }}>range</span><span style={{ color: "#d4d4d4" }}>(</span><span style={{ color: "#B5CEA8" }}>5</span><span style={{ color: "#d4d4d4" }}>):</span>{"\n"}
                {"    "}<span style={{ color: "#DCDCAA" }}>print</span><span style={{ color: "#d4d4d4" }}>(</span><span style={{ color: "#CE9178" }}>"Hello!"</span><span style={{ color: "#d4d4d4" }}>)</span>
              </pre>

              {/* Labeled callouts */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                {/* Blue callout */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#EFF6FF", borderRadius: 10, padding: "12px 14px", border: "1px solid #BFDBFE" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#3B82F6", flexShrink: 0, marginTop: 3 }} />
                  <div style={{ fontSize: 13, color: "#1E3A5F", lineHeight: 1.6 }}>
                    <code style={{ background: "#DBEAFE", padding: "1px 6px", borderRadius: 4, color: "#1D4ED8", fontSize: 12 }}>for i in range(5):</code>
                    {" "}— <strong>This starts the loop.</strong> <code style={{ background: "#DBEAFE", padding: "1px 5px", borderRadius: 4, color: "#1D4ED8", fontSize: 12 }}>range(5)</code> means repeat 5 times.
                  </div>
                </div>
                {/* Green callout */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#F0FDF4", borderRadius: 10, padding: "12px 14px", border: "1px solid #BBF7D0" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#22C55E", flexShrink: 0, marginTop: 3 }} />
                  <div style={{ fontSize: 13, color: "#14532D", lineHeight: 1.6 }}>
                    <code style={{ background: "#DCFCE7", padding: "1px 6px", borderRadius: 4, color: "#15803D", fontSize: 12 }}>{"    "}print("Hello!")</code>
                    {" "}— <strong>This is what repeats.</strong> See the 4 spaces before it? That's called <strong>indentation</strong> — Python requires it!
                  </div>
                </div>
                {/* Amber warning callout */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#FFFBEB", borderRadius: 10, padding: "12px 14px", border: "1px solid #F59E0B" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#F59E0B", flexShrink: 0, marginTop: 3 }} />
                  <div style={{ fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
                    The 4 spaces (or one <strong>Tab key</strong> press) before <code style={{ background: "#FEF3C7", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>print</code> are <strong>NOT optional</strong>. Without them Python will show an <code style={{ background: "#FEF3C7", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>IndentationError</code> and your code won't run.
                  </div>
                </div>
              </div>

              <button onClick={() => setWatchExplained(true)} style={{
                width: "100%", background: color, color: "#fff", border: "none", borderRadius: 12,
                padding: "13px", cursor: "pointer", fontSize: 14, fontWeight: 800,
                boxShadow: `0 4px 14px ${color}40`,
              }}>Got it! Show me it running →</button>
            </>
          )}

          {/* ── ANIMATION sub-phase ── */}
          {watchExplained && (
            <>
              <div style={{ fontWeight: 800, color: "#44403C", fontSize: 15, marginBottom: 4 }}>👀 Watch how a for loop works</div>
              <div style={{ fontSize: 13, color: "#78716C", marginBottom: 14 }}>
                Watch <code style={{ background: "#F0FDFA", padding: "1px 6px", borderRadius: 4, color: "#0D9488" }}>i</code> count from 0 to 4, printing "Hello!" each time.
              </div>

              {/* Syntax-coloured code preview */}
              <pre style={{ background: "#1e1e1e", borderRadius: 10, padding: "14px 16px", marginBottom: 14, fontFamily: "'Courier New', monospace", fontSize: 13, lineHeight: 2, margin: "0 0 14px" }}><span style={{ color: "#569CD6" }}>for </span><span style={{ color: "#9CDCFE" }}>i </span><span style={{ color: "#569CD6" }}>in </span><span style={{ color: "#DCDCAA" }}>range</span><span style={{ color: "#d4d4d4" }}>(</span><span style={{ color: "#B5CEA8" }}>5</span><span style={{ color: "#d4d4d4" }}>):</span>{"\n"}{"    "}<span style={{ color: "#DCDCAA" }}>print</span><span style={{ color: "#d4d4d4" }}>(</span><span style={{ color: "#CE9178" }}>"Hello!"</span><span style={{ color: "#d4d4d4" }}>)</span></pre>

              {/* Counter + output */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px", border: "1px solid #0D948840" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#0D9488", letterSpacing: 1, marginBottom: 10 }}>LOOP VARIABLE</div>
                  {watchStep === -1 && (
                    <div style={{ background: "#1e293b", borderRadius: 8, padding: "10px 14px", fontFamily: "monospace", fontSize: 14, color: "#374151", textAlign: "center" }}>waiting...</div>
                  )}
                  {watchStep >= 0 && watchStep < 5 && (
                    <div key={watchStep} style={{
                      background: "#134e4a", borderRadius: 8, padding: "10px 14px",
                      fontFamily: "monospace", fontSize: 22, fontWeight: 800, color: "#5EEAD4",
                      textAlign: "center", animation: "csBuildersCount 0.35s ease-out",
                    }}>i = {watchStep}</div>
                  )}
                  {watchStep === 5 && (
                    <div style={{ background: "#1e293b", borderRadius: 8, padding: "10px 14px", fontFamily: "monospace", fontSize: 13, color: "#6EE7B7", textAlign: "center" }}>loop done ✓</div>
                  )}
                  {watchStep >= 0 && watchStep < 5 && (
                    <div style={{ fontSize: 11, color: "#6B7280", marginTop: 8, textAlign: "center" }}>
                      {4 - watchStep} more iteration{4 - watchStep !== 1 ? "s" : ""} left
                    </div>
                  )}
                </div>

                <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px", border: "1px solid #1e293b", minHeight: 110 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", letterSpacing: 1, marginBottom: 10 }}>OUTPUT</div>
                  {hellos.length === 0 && <div style={{ fontFamily: "monospace", fontSize: 13, color: "#374151" }}>...</div>}
                  {hellos.map((h, idx) => (
                    <div key={idx} style={{
                      fontFamily: "monospace", fontSize: 13, color: "#86EFAC", lineHeight: 1.7,
                      animation: idx === hellos.length - 1 ? "csHelloIn 0.25s ease-out" : "none",
                    }}>{h}</div>
                  ))}
                </div>
              </div>

              {watchStep === 5 && (
                <div style={{ background: "#F0FDFA", border: "1px solid #0D948840", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#0F766E" }}>
                  Now write your own for loop from scratch. Remember the indentation!
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={replayWatch} style={{
                  background: "#F0FDFA", color: color, border: `1px solid ${color}40`,
                  borderRadius: 10, padding: "9px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600,
                }}>Watch again 🔄</button>
                {watchStep === 5 && (
                  <button onClick={() => setLessonPhase("think")} style={{
                    flex: 1, background: color, color: "#fff", border: "none", borderRadius: 10,
                    padding: "9px", cursor: "pointer", fontSize: 13, fontWeight: 800,
                    boxShadow: `0 4px 14px ${color}40`,
                  }}>I get it, let's try! →</button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── THINK + CHECK ── */}
      {(lessonPhase === "think" || lessonPhase === "check") && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "20px", border: "1px solid #E7E5E4" }}>
          <div style={{ background: "#F0FDFA", borderRadius: 12, padding: "12px 16px", marginBottom: 16, border: "1px solid #0D948830" }}>
            <div style={{ fontWeight: 700, color: "#0F766E", marginBottom: 4 }}>🧠 Brain First!</div>
            <div style={{ fontSize: 13, color: "#44403C", lineHeight: 1.6 }}>
              Write a <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>for</code> loop in Python that prints <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>Hello!</code> exactly <strong>5 times</strong>.
            </div>
          </div>

          <textarea
            value={code}
            onChange={e => setCode(e.target.value)}
            readOnly={isRunning}
            onKeyDown={e => {
              if (e.key === "Tab") {
                e.preventDefault();
                const s = e.target.selectionStart;
                const newVal = code.substring(0, s) + "    " + code.substring(e.target.selectionEnd);
                setCode(newVal);
                requestAnimationFrame(() => { e.target.selectionStart = e.target.selectionEnd = s + 4; });
              }
            }}
            placeholder="# Write your for loop here"
            spellCheck={false}
            style={{
              width: "100%", boxSizing: "border-box",
              background: "#1e1e1e", color: "#d4d4d4",
              fontFamily: "'Courier New', Courier, monospace",
              fontSize: 14, padding: "16px", borderRadius: 10,
              minHeight: 120, lineHeight: 1.8,
              border: "2px solid #3e3e3e", outline: "none", resize: "vertical",
              marginBottom: 12,
            }}
          />

          {failMsg && (
            <div style={{ background: "#FEF3C7", border: "1px solid #F59E0B40", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#92400E" }}>
              ⚠️ {failMsg}
            </div>
          )}

          {pyError && pyErrorType === "indent" && (
            <>
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 10, fontSize: 13, color: "#7F1D1D", lineHeight: 1.7 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>⚠️ Indentation Error</div>
                Python can't find the indented block. Make sure the line starting with <code style={{ background: "#FEE2E2", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>print</code> has <strong>4 spaces or a Tab</strong> before it. Your loop header ends with a colon (<code style={{ background: "#FEE2E2", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>:</code>) — the next line must be indented.
              </div>
              <div style={{ background: "#1e1e1e", borderRadius: 8, padding: "12px 16px", marginBottom: 12, fontFamily: "'Courier New', monospace", fontSize: 13, lineHeight: 2 }}>
                <div>
                  <span style={{ color: "#569CD6" }}>for </span>
                  <span style={{ color: "#9CDCFE" }}>i </span>
                  <span style={{ color: "#569CD6" }}>in </span>
                  <span style={{ color: "#DCDCAA" }}>range</span>
                  <span style={{ color: "#d4d4d4" }}>(</span>
                  <span style={{ color: "#B5CEA8" }}>5</span>
                  <span style={{ color: "#d4d4d4" }}>):</span>
                </div>
                <div>
                  <span style={{ color: "#d4d4d4" }}>{"    "}</span>
                  <span style={{ color: "#DCDCAA" }}>print</span>
                  <span style={{ color: "#d4d4d4" }}>(</span>
                  <span style={{ color: "#CE9178" }}>'Hello!'</span>
                  <span style={{ color: "#d4d4d4" }}>)</span>
                  <span style={{ color: "#6A9955", marginLeft: 12 }}>← 4 spaces here</span>
                </div>
              </div>
            </>
          )}

          {pyError && pyErrorType === "syntax" && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 12, fontSize: 13, color: "#7F1D1D", lineHeight: 1.7 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>⚠️ Syntax Error</div>
              Something about how the code is written isn't quite right. Check for missing <strong>colons</strong>, <strong>brackets</strong>, or <strong>quote marks</strong>.
            </div>
          )}

          {pyError && pyErrorType === "other" && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 12, fontSize: 13, color: "#7F1D1D", lineHeight: 1.7 }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>⚠️ Python found a problem in your code</div>
              <div style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: "#991B1B", background: "#FEE2E2", borderRadius: 6, padding: "6px 10px" }}>{pyError}</div>
            </div>
          )}

          {failMsg && actualOutput !== null && (
            <div style={{ background: "#0f172a", borderRadius: 10, padding: "12px 14px", marginBottom: 12, border: "1px solid #374151" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", letterSpacing: 1, marginBottom: 8 }}>YOUR OUTPUT</div>
              {actualOutput.length > 0
                ? actualOutput.map((line, i) => <div key={i} style={{ fontFamily: "monospace", fontSize: 13, color: "#86EFAC", lineHeight: 1.7 }}>{line}</div>)
                : <div style={{ fontFamily: "monospace", fontSize: 13, color: "#4B5563" }}>(no output — did you use print()?)</div>
              }
            </div>
          )}

          <button
            onClick={runCode}
            disabled={code.length < 20 || isRunning}
            style={{
              width: "100%", border: "none", borderRadius: 12, padding: "13px",
              cursor: code.length < 20 || isRunning ? "default" : "pointer",
              fontSize: 14, fontWeight: 800, transition: "all 0.15s",
              background: isRunning ? `${color}99` : code.length < 20 ? "#E7E5E4" : color,
              color: code.length < 20 && !isRunning ? "#A8A29E" : "#fff",
            }}
          >
            {runStatus === "loading" ? "Loading Python... 🐍" : runStatus === "running" ? "Running your code..." : code.length < 20 ? "✍️ Write at least 20 characters first..." : "Run my code →"}
          </button>
        </div>
      )}

      {/* ── CELEBRATE ── */}
      {lessonPhase === "celebrate" && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "24px", border: "1px solid #E7E5E4", textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 10 }}>✅</div>
          <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 24, fontWeight: 900, color: "#44403C", margin: "0 0 8px" }}>
            Your loop worked! 🎉
          </h2>
          <p style={{ fontSize: 14, color: "#78716C", lineHeight: 1.7, marginBottom: 20 }}>
            One loop → five lines of output. That's the power of code. You just ran real Python in your browser.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 22, textAlign: "left" }}>
            <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px", border: "2px solid #059669" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#059669", letterSpacing: 1, marginBottom: 8 }}>✓ YOUR OUTPUT</div>
              {(actualOutput || []).map((line, i) => (
                <div key={i} style={{ fontFamily: "monospace", fontSize: 13, color: "#86EFAC", lineHeight: 1.7 }}>{line}</div>
              ))}
            </div>
            <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px", border: `1px solid ${color}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: color, letterSpacing: 1, marginBottom: 8 }}>EXPECTED</div>
              {["Hello!", "Hello!", "Hello!", "Hello!", "Hello!"].map((line, i) => (
                <div key={i} style={{ fontFamily: "monospace", fontSize: 13, color: "#5EEAD4", lineHeight: 1.7 }}>{line}</div>
              ))}
            </div>
          </div>

          <div style={{
            display: "inline-flex", alignItems: "center", gap: 12,
            background: "linear-gradient(135deg, #F0FDFA, #CCFBF1)",
            borderRadius: 14, padding: "14px 22px", marginBottom: 24, border: `1px solid ${color}40`,
          }}>
            <span style={{ fontSize: 28 }}>⚙️</span>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontWeight: 800, color: "#0F766E", fontSize: 18 }}>+80 XP earned!</div>
              <div style={{ fontSize: 12, color: color }}>Loop Master badge unlocked 🏅</div>
            </div>
          </div>

          <div>
            <button onClick={handleComplete} style={{
              background: color, color: "#fff", border: "none", borderRadius: 14,
              padding: "14px 36px", cursor: "pointer", fontSize: 16, fontWeight: 800,
              boxShadow: `0 4px 20px ${color}40`,
            }}>Next Lesson →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BUILDERS LESSON 2 — If, Elif, Else (Feed the Dragon) ─────────────────────

function BuildersLesson2({ onNav, progress, setProgress }) {
  const color = "#0D9488";
  const [lessonPhase, setLessonPhase] = useState("watch"); // watch | think | check | celebrate

  // Watch phase
  const [watchExplained, setWatchExplained] = useState(false);
  const [animStep, setAnimStep] = useState(-1); // -1 waiting, 0 hunger 95, 1 hunger 50, 2 hunger 10, 3 done
  const [watchKey, setWatchKey] = useState(0);
  const animIntervalRef = useRef(null);

  // Think / Check phase
  const [code, setCode] = useState("");
  const [runStatus, setRunStatus] = useState(null); // null | "loading" | "running"
  const [testResults, setTestResults] = useState(null);
  const [failMsg, setFailMsg] = useState(null);
  const [pyError, setPyError] = useState(null);
  const [pyErrorType, setPyErrorType] = useState(null);

  const hungers = [95, 50, 10];
  const moods = ["HANGRY", "PECKISH", "SLEEPY"];
  const outputs = ["ROAR!", "grumble", "zzz"];

  // Inject animations
  useEffect(() => {
    const el = document.createElement("style");
    el.id = "cs-builders2-anim";
    el.textContent = `
      @keyframes csBranchPulse {
        0%   { background-color: rgba(20, 83, 79, 0.55); }
        100% { background-color: rgba(20, 83, 79, 0.30); }
      }
      @keyframes csStateIn {
        0%   { opacity: 0; transform: translateY(6px); }
        100% { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(el);
    return () => { const old = document.getElementById("cs-builders2-anim"); if (old) old.remove(); };
  }, []);

  // Watch animation driver — only starts after user reads the explanation
  useEffect(() => {
    if (lessonPhase !== "watch" || !watchExplained) return;
    setAnimStep(-1);
    const startTimer = setTimeout(() => {
      setAnimStep(0);
      let i = 1;
      animIntervalRef.current = setInterval(() => {
        if (i <= 2) {
          setAnimStep(i);
          i++;
        } else {
          clearInterval(animIntervalRef.current);
          setAnimStep(3);
        }
      }, 1200);
    }, 400);
    return () => { clearTimeout(startTimer); clearInterval(animIntervalRef.current); };
  }, [lessonPhase, watchKey, watchExplained]);

  const replayWatch = () => {
    clearInterval(animIntervalRef.current);
    setWatchKey(k => k + 1);
  };

  const runCode = async () => {
    setFailMsg(null);
    setPyError(null);
    setPyErrorType(null);
    setTestResults(null);
    setLessonPhase("check");
    setRunStatus("loading");

    let pyodide;
    try {
      pyodide = await getPyodide();
    } catch {
      setFailMsg("Couldn't load Python - check your connection and try again.");
      setRunStatus(null);
      setLessonPhase("think");
      return;
    }
    setRunStatus("running");

    const testCases = [
      { hunger: 95, expected: "ROAR!" },
      { hunger: 50, expected: "grumble" },
      { hunger: 10, expected: "zzz" },
    ];

    const results = [];
    let caughtError = false;

    for (const tc of testCases) {
      try {
        pyodide.globals.set("_user_code", code);
        pyodide.globals.set("hunger", tc.hunger);
        const output = await pyodide.runPythonAsync(`
import sys, io
_buf = io.StringIO()
sys.stdout = _buf
try:
    exec(compile(_user_code, '<student>', 'exec'))
finally:
    sys.stdout = sys.__stdout__
_buf.getvalue()
        `);
        const actual = output.trim();
        results.push({ ...tc, actual, pass: actual === tc.expected });
      } catch (err) {
        const fullMsg = err.message || "";
        const technical = fullMsg.split("\n").filter(l => l.trim()).slice(-2).join(" - ");
        let errorType = "other";
        if (fullMsg.includes("IndentationError")) errorType = "indent";
        else if (fullMsg.includes("SyntaxError")) errorType = "syntax";
        setPyErrorType(errorType);
        setPyError(technical);
        setLessonPhase("think");
        caughtError = true;
        break;
      }
    }

    if (!caughtError) {
      setTestResults(results);
      const allPass = results.every(r => r.pass);
      if (allPass) {
        setLessonPhase("celebrate");
      } else {
        setFailMsg("Your code ran, but the dragon didn't react correctly for every hunger level. Check the results below.");
        setLessonPhase("think");
      }
    }
    setRunStatus(null);
  };

  const handleComplete = () => {
    const updated = {
      ...progress,
      xp: progress.xp + 80,
      completedModules: [...new Set([...progress.completedModules, "middle-2"])],
    };
    setProgress(updated);
    saveProgress(updated);
    onNav("dashboard");
  };

  const isRunning = runStatus !== null;

  const bPhaseSteps = ["watch", "think", "check", "celebrate"];
  const handleBack = () => {
    const idx = bPhaseSteps.indexOf(lessonPhase);
    if (idx <= 0) { onNav("dashboard"); return; }
    const prev = bPhaseSteps[idx - 1];
    if (prev === "watch") { setLessonPhase("watch"); setWatchExplained(false); setAnimStep(-1); }
    else { setLessonPhase(prev); }
  };

  return (
    <div style={{ maxWidth: 660, margin: "0 auto", padding: "24px 20px" }}>
      <button onClick={handleBack} style={{ background: "none", border: "none", cursor: "pointer", color: "#78716C", fontSize: 14, fontWeight: 600, marginBottom: 18 }}>← Back</button>

      <div style={{ background: "linear-gradient(135deg, #0D9488, #0F766E)", borderRadius: 20, padding: "22px 26px", marginBottom: 20, color: "#fff" }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>MODULE 2 · IF, ELIF, ELSE</div>
        <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Georgia', serif" }}>Feed the Dragon 🐲</div>
      </div>

      <LessonProgress phase={lessonPhase} color={color} onStepClick={(step) => {
        if (step === "watch") {
          setLessonPhase("watch");
          setWatchExplained(false);
          setAnimStep(-1);
        } else {
          setLessonPhase(step);
        }
      }} />

      {/* ── WATCH ── */}
      {lessonPhase === "watch" && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "20px", border: "1px solid #E7E5E4" }}>

          {/* ── EXPLAIN sub-phase ── */}
          {!watchExplained && (
            <>
              <div style={{ fontWeight: 800, color: "#44403C", fontSize: 16, marginBottom: 16 }}>
                Here's how if, elif, else works in Python
              </div>

              <pre style={{ background: "#1e1e1e", borderRadius: 10, padding: "16px 18px", marginBottom: 18, fontFamily: "'Courier New', monospace", fontSize: 14, lineHeight: 2.1, margin: "0 0 18px", overflowX: "auto" }}>
                <span style={{ color: "#569CD6" }}>if </span><span style={{ color: "#9CDCFE" }}>hunger </span><span style={{ color: "#d4d4d4" }}>{">"} </span><span style={{ color: "#B5CEA8" }}>80</span><span style={{ color: "#d4d4d4" }}>:</span>{"\n"}
                {"    "}<span style={{ color: "#DCDCAA" }}>print</span><span style={{ color: "#d4d4d4" }}>(</span><span style={{ color: "#CE9178" }}>"ROAR!"</span><span style={{ color: "#d4d4d4" }}>)</span>{"\n"}
                <span style={{ color: "#569CD6" }}>elif </span><span style={{ color: "#9CDCFE" }}>hunger </span><span style={{ color: "#d4d4d4" }}>{">"} </span><span style={{ color: "#B5CEA8" }}>30</span><span style={{ color: "#d4d4d4" }}>:</span>{"\n"}
                {"    "}<span style={{ color: "#DCDCAA" }}>print</span><span style={{ color: "#d4d4d4" }}>(</span><span style={{ color: "#CE9178" }}>"grumble"</span><span style={{ color: "#d4d4d4" }}>)</span>{"\n"}
                <span style={{ color: "#569CD6" }}>else</span><span style={{ color: "#d4d4d4" }}>:</span>{"\n"}
                {"    "}<span style={{ color: "#DCDCAA" }}>print</span><span style={{ color: "#d4d4d4" }}>(</span><span style={{ color: "#CE9178" }}>"zzz"</span><span style={{ color: "#d4d4d4" }}>)</span>
              </pre>

              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                {/* Blue callout */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#EFF6FF", borderRadius: 10, padding: "12px 14px", border: "1px solid #BFDBFE" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#3B82F6", flexShrink: 0, marginTop: 3 }} />
                  <div style={{ fontSize: 13, color: "#1E3A5F", lineHeight: 1.6 }}>
                    <code style={{ background: "#DBEAFE", padding: "1px 6px", borderRadius: 4, color: "#1D4ED8", fontSize: 12 }}>if hunger {">"} 80:</code>
                    {" "}- This is checked FIRST. If hunger is more than 80, the dragon roars.
                  </div>
                </div>
                {/* Green callout */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#F0FDF4", borderRadius: 10, padding: "12px 14px", border: "1px solid #BBF7D0" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#22C55E", flexShrink: 0, marginTop: 3 }} />
                  <div style={{ fontSize: 13, color: "#14532D", lineHeight: 1.6 }}>
                    <code style={{ background: "#DCFCE7", padding: "1px 6px", borderRadius: 4, color: "#15803D", fontSize: 12 }}>elif hunger {">"} 30:</code>
                    {" "}- "elif" means "else if". It's only checked if the <code style={{ background: "#DCFCE7", padding: "1px 5px", borderRadius: 4, color: "#15803D", fontSize: 12 }}>if</code> above was False. Peckish dragons grumble.
                  </div>
                </div>
                {/* Amber callout */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#FFFBEB", borderRadius: 10, padding: "12px 14px", border: "1px solid #F59E0B" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#F59E0B", flexShrink: 0, marginTop: 3 }} />
                  <div style={{ fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
                    <code style={{ background: "#FEF3C7", padding: "1px 6px", borderRadius: 4, color: "#B45309", fontSize: 12 }}>else:</code>
                    {" "}- The catch-all. Runs when nothing above matched. A full dragon snores.
                  </div>
                </div>
              </div>

              <button onClick={() => setWatchExplained(true)} style={{
                width: "100%", background: color, color: "#fff", border: "none", borderRadius: 12,
                padding: "13px", cursor: "pointer", fontSize: 14, fontWeight: 800,
                boxShadow: `0 4px 14px ${color}40`,
              }}>Got it! Show me the dragon →</button>
            </>
          )}

          {/* ── ANIMATION sub-phase ── */}
          {watchExplained && (
            <>
              <div style={{ fontWeight: 800, color: "#44403C", fontSize: 15, marginBottom: 4 }}>👀 Watch the dragon react as hunger drops</div>
              <div style={{ fontSize: 13, color: "#78716C", marginBottom: 14 }}>
                See which branch fires for each hunger level.
              </div>

              {/* Branch-highlighted code block */}
              <div style={{ background: "#1e1e1e", borderRadius: 10, padding: "10px 0", marginBottom: 14, fontFamily: "'Courier New', monospace", fontSize: 13, lineHeight: 2 }}>
                {/* if branch */}
                <div key={`if-${watchKey}-${animStep === 0 ? "on" : "off"}`} style={{
                  padding: "2px 16px",
                  background: animStep === 0 ? "rgba(20, 83, 79, 0.45)" : "transparent",
                  animation: animStep === 0 ? "csBranchPulse 0.35s ease-out" : "none",
                }}>
                  <span style={{ color: "#569CD6" }}>if </span>
                  <span style={{ color: "#9CDCFE" }}>hunger </span>
                  <span style={{ color: "#d4d4d4" }}>{">"} </span>
                  <span style={{ color: "#B5CEA8" }}>80</span>
                  <span style={{ color: "#d4d4d4" }}>:</span>
                </div>
                <div style={{
                  padding: "2px 16px",
                  background: animStep === 0 ? "rgba(20, 83, 79, 0.45)" : "transparent",
                }}>
                  <span style={{ color: "#d4d4d4" }}>{"    "}</span>
                  <span style={{ color: "#DCDCAA" }}>print</span>
                  <span style={{ color: "#d4d4d4" }}>(</span>
                  <span style={{ color: "#CE9178" }}>"ROAR!"</span>
                  <span style={{ color: "#d4d4d4" }}>)</span>
                </div>
                {/* elif branch */}
                <div key={`elif-${watchKey}-${animStep === 1 ? "on" : "off"}`} style={{
                  padding: "2px 16px",
                  background: animStep === 1 ? "rgba(20, 83, 79, 0.45)" : "transparent",
                  animation: animStep === 1 ? "csBranchPulse 0.35s ease-out" : "none",
                }}>
                  <span style={{ color: "#569CD6" }}>elif </span>
                  <span style={{ color: "#9CDCFE" }}>hunger </span>
                  <span style={{ color: "#d4d4d4" }}>{">"} </span>
                  <span style={{ color: "#B5CEA8" }}>30</span>
                  <span style={{ color: "#d4d4d4" }}>:</span>
                </div>
                <div style={{
                  padding: "2px 16px",
                  background: animStep === 1 ? "rgba(20, 83, 79, 0.45)" : "transparent",
                }}>
                  <span style={{ color: "#d4d4d4" }}>{"    "}</span>
                  <span style={{ color: "#DCDCAA" }}>print</span>
                  <span style={{ color: "#d4d4d4" }}>(</span>
                  <span style={{ color: "#CE9178" }}>"grumble"</span>
                  <span style={{ color: "#d4d4d4" }}>)</span>
                </div>
                {/* else branch */}
                <div key={`else-${watchKey}-${animStep === 2 ? "on" : "off"}`} style={{
                  padding: "2px 16px",
                  background: animStep === 2 ? "rgba(20, 83, 79, 0.45)" : "transparent",
                  animation: animStep === 2 ? "csBranchPulse 0.35s ease-out" : "none",
                }}>
                  <span style={{ color: "#569CD6" }}>else</span>
                  <span style={{ color: "#d4d4d4" }}>:</span>
                </div>
                <div style={{
                  padding: "2px 16px",
                  background: animStep === 2 ? "rgba(20, 83, 79, 0.45)" : "transparent",
                }}>
                  <span style={{ color: "#d4d4d4" }}>{"    "}</span>
                  <span style={{ color: "#DCDCAA" }}>print</span>
                  <span style={{ color: "#d4d4d4" }}>(</span>
                  <span style={{ color: "#CE9178" }}>"zzz"</span>
                  <span style={{ color: "#d4d4d4" }}>)</span>
                </div>
              </div>

              {/* Hunger + output */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px", border: "1px solid #0D948840" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#0D9488", letterSpacing: 1, marginBottom: 10 }}>HUNGER</div>
                  {animStep === -1 && (
                    <div style={{ background: "#1e293b", borderRadius: 8, padding: "10px 14px", fontFamily: "monospace", fontSize: 14, color: "#374151", textAlign: "center" }}>waiting...</div>
                  )}
                  {animStep >= 0 && animStep < 3 && (
                    <>
                      <div key={`h-${animStep}`} style={{
                        background: "#134e4a", borderRadius: 8, padding: "10px 14px",
                        fontFamily: "monospace", fontSize: 32, fontWeight: 800, color: "#5EEAD4",
                        textAlign: "center", animation: "csStateIn 0.25s ease-out",
                      }}>{hungers[animStep]}</div>
                      <div style={{ fontSize: 11, color: "#6B7280", marginTop: 8, textAlign: "center", letterSpacing: 1, fontWeight: 700 }}>
                        {moods[animStep]}
                      </div>
                    </>
                  )}
                  {animStep === 3 && (
                    <div style={{ background: "#1e293b", borderRadius: 8, padding: "10px 14px", fontFamily: "monospace", fontSize: 13, color: "#6EE7B7", textAlign: "center" }}>done ✓</div>
                  )}
                </div>

                <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px", border: "1px solid #1e293b", minHeight: 110 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", letterSpacing: 1, marginBottom: 10 }}>OUTPUT</div>
                  {animStep === -1 && <div style={{ fontFamily: "monospace", fontSize: 13, color: "#374151" }}>...</div>}
                  {outputs.slice(0, animStep === 3 ? 3 : Math.max(0, animStep + 1)).map((line, idx) => {
                    const isNewest = (animStep < 3 && idx === animStep);
                    return (
                      <div key={`out-${watchKey}-${idx}`} style={{
                        fontFamily: "monospace", fontSize: 13, color: "#86EFAC", lineHeight: 1.7,
                        animation: isNewest ? "csStateIn 0.25s ease-out" : "none",
                      }}>{line}</div>
                    );
                  })}
                </div>
              </div>

              {animStep === 3 && (
                <div style={{ background: "#F0FDFA", border: "1px solid #0D948840", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#0F766E" }}>
                  Each hunger level hit a different branch. Now write your own →
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={replayWatch} style={{
                  background: "#F0FDFA", color: color, border: `1px solid ${color}40`,
                  borderRadius: 10, padding: "9px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600,
                }}>Watch again 🔄</button>
                {animStep === 3 && (
                  <button onClick={() => setLessonPhase("think")} style={{
                    flex: 1, background: color, color: "#fff", border: "none", borderRadius: 10,
                    padding: "9px", cursor: "pointer", fontSize: 13, fontWeight: 800,
                    boxShadow: `0 4px 14px ${color}40`,
                  }}>I get it, let's try! →</button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── THINK + CHECK ── */}
      {(lessonPhase === "think" || lessonPhase === "check") && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "20px", border: "1px solid #E7E5E4" }}>
          <div style={{ background: "#F0FDFA", borderRadius: 12, padding: "12px 16px", marginBottom: 14, border: "1px solid #0D948830" }}>
            <div style={{ fontWeight: 700, color: "#0F766E", marginBottom: 4 }}>🧠 Brain First!</div>
            <div style={{ fontSize: 13, color: "#44403C", lineHeight: 1.6 }}>
              Write code that makes the dragon react to its <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>hunger</code>. The variable <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>hunger</code> is already set for you - you don't need to create it. Use <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>if</code>, <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>elif</code>, and <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>else</code>.
            </div>
          </div>

          <div style={{ background: "#FAFAF9", borderRadius: 10, padding: "12px 14px", border: "1px solid #E7E5E4", marginBottom: 12, fontSize: 13, color: "#44403C", lineHeight: 1.9 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#78716C", letterSpacing: 1, marginBottom: 6 }}>EXPECTED BEHAVIOUR</div>
            <div>When <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>hunger</code> is over 80: print <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>"ROAR!"</code></div>
            <div>When <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>hunger</code> is over 30: print <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>"grumble"</code></div>
            <div>Otherwise: print <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>"zzz"</code></div>
          </div>

          <textarea
            value={code}
            onChange={e => setCode(e.target.value)}
            readOnly={isRunning}
            onKeyDown={e => {
              if (e.key === "Tab") {
                e.preventDefault();
                const s = e.target.selectionStart;
                const newVal = code.substring(0, s) + "    " + code.substring(e.target.selectionEnd);
                setCode(newVal);
                requestAnimationFrame(() => { e.target.selectionStart = e.target.selectionEnd = s + 4; });
              }
            }}
            placeholder="# Write your if / elif / else here"
            spellCheck={false}
            style={{
              width: "100%", boxSizing: "border-box",
              background: "#1e1e1e", color: "#d4d4d4",
              fontFamily: "'Courier New', Courier, monospace",
              fontSize: 14, padding: "16px", borderRadius: 10,
              minHeight: 140, lineHeight: 1.8,
              border: "2px solid #3e3e3e", outline: "none", resize: "vertical",
              marginBottom: 12,
            }}
          />

          {failMsg && (
            <div style={{ background: "#FEF3C7", border: "1px solid #F59E0B40", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#92400E" }}>
              ⚠️ {failMsg}
            </div>
          )}

          {pyError && pyErrorType === "indent" && (
            <>
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 10, fontSize: 13, color: "#7F1D1D", lineHeight: 1.7 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>⚠️ Indentation Error</div>
                Python can't find the indented block. After every line ending with a colon (<code style={{ background: "#FEE2E2", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>:</code>) the next line must have <strong>4 spaces or a Tab</strong>.
              </div>
              <div style={{ background: "#1e1e1e", borderRadius: 8, padding: "12px 16px", marginBottom: 12, fontFamily: "'Courier New', monospace", fontSize: 13, lineHeight: 2 }}>
                <div>
                  <span style={{ color: "#569CD6" }}>if </span>
                  <span style={{ color: "#9CDCFE" }}>hunger </span>
                  <span style={{ color: "#d4d4d4" }}>{">"} </span>
                  <span style={{ color: "#B5CEA8" }}>80</span>
                  <span style={{ color: "#d4d4d4" }}>:</span>
                </div>
                <div>
                  <span style={{ color: "#d4d4d4" }}>{"    "}</span>
                  <span style={{ color: "#DCDCAA" }}>print</span>
                  <span style={{ color: "#d4d4d4" }}>(</span>
                  <span style={{ color: "#CE9178" }}>'ROAR!'</span>
                  <span style={{ color: "#d4d4d4" }}>)</span>
                  <span style={{ color: "#6A9955", marginLeft: 12 }}>← 4 spaces here</span>
                </div>
                <div>
                  <span style={{ color: "#569CD6" }}>elif </span>
                  <span style={{ color: "#9CDCFE" }}>hunger </span>
                  <span style={{ color: "#d4d4d4" }}>{">"} </span>
                  <span style={{ color: "#B5CEA8" }}>30</span>
                  <span style={{ color: "#d4d4d4" }}>:</span>
                </div>
                <div>
                  <span style={{ color: "#d4d4d4" }}>{"    "}</span>
                  <span style={{ color: "#DCDCAA" }}>print</span>
                  <span style={{ color: "#d4d4d4" }}>(</span>
                  <span style={{ color: "#CE9178" }}>'grumble'</span>
                  <span style={{ color: "#d4d4d4" }}>)</span>
                  <span style={{ color: "#6A9955", marginLeft: 12 }}>← 4 spaces here</span>
                </div>
                <div>
                  <span style={{ color: "#569CD6" }}>else</span>
                  <span style={{ color: "#d4d4d4" }}>:</span>
                </div>
                <div>
                  <span style={{ color: "#d4d4d4" }}>{"    "}</span>
                  <span style={{ color: "#DCDCAA" }}>print</span>
                  <span style={{ color: "#d4d4d4" }}>(</span>
                  <span style={{ color: "#CE9178" }}>'zzz'</span>
                  <span style={{ color: "#d4d4d4" }}>)</span>
                  <span style={{ color: "#6A9955", marginLeft: 12 }}>← 4 spaces here</span>
                </div>
              </div>
            </>
          )}

          {pyError && pyErrorType === "syntax" && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 12, fontSize: 13, color: "#7F1D1D", lineHeight: 1.7 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>⚠️ Syntax Error</div>
              Something about how the code is written isn't quite right. Check for missing <strong>colons</strong>, <strong>brackets</strong>, or <strong>quote marks</strong>.
            </div>
          )}

          {pyError && pyErrorType === "other" && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 12, fontSize: 13, color: "#7F1D1D", lineHeight: 1.7 }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>⚠️ Python found a problem in your code</div>
              <div style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: "#991B1B", background: "#FEE2E2", borderRadius: 6, padding: "6px 10px" }}>{pyError}</div>
            </div>
          )}

          {failMsg && testResults && (
            <div style={{ background: "#0f172a", borderRadius: 10, padding: "12px", marginBottom: 12, border: "1px solid #374151" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", letterSpacing: 1, marginBottom: 10 }}>TEST RESULTS</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {testResults.map((r, i) => (
                  <div key={i} style={{
                    display: "grid", gridTemplateColumns: "auto 1fr 1fr auto", gap: 10, alignItems: "center",
                    background: "#1e293b", borderRadius: 8, padding: "8px 12px",
                    border: `1px solid ${r.pass ? "#0D948840" : "#F59E0B40"}`,
                  }}>
                    <span style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: "#fff" }}>hunger={r.hunger}</span>
                    <span style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: "#5EEAD4" }}>→ {r.expected}</span>
                    <span style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: r.pass ? "#86EFAC" : "#FCD34D" }}>{r.actual || "(no output)"}</span>
                    <span style={{ fontSize: 14, fontWeight: 900, color: r.pass ? "#10B981" : "#F59E0B" }}>{r.pass ? "✓" : "✗"}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={runCode}
            disabled={code.length < 20 || isRunning}
            style={{
              width: "100%", border: "none", borderRadius: 12, padding: "13px",
              cursor: code.length < 20 || isRunning ? "default" : "pointer",
              fontSize: 14, fontWeight: 800, transition: "all 0.15s",
              background: isRunning ? `${color}99` : code.length < 20 ? "#E7E5E4" : color,
              color: code.length < 20 && !isRunning ? "#A8A29E" : "#fff",
            }}
          >
            {runStatus === "loading" ? "Loading Python... 🐍" : runStatus === "running" ? "Running your code..." : code.length < 20 ? "✍️ Write at least 20 characters first..." : "Run my code →"}
          </button>
        </div>
      )}

      {/* ── CELEBRATE ── */}
      {lessonPhase === "celebrate" && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "24px", border: "1px solid #E7E5E4", textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 10 }}>✅</div>
          <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 24, fontWeight: 900, color: "#44403C", margin: "0 0 8px" }}>
            The dragon is happy! 🐲
          </h2>
          <p style={{ fontSize: 14, color: "#78716C", lineHeight: 1.7, marginBottom: 20 }}>
            Your code handled all three hunger levels. That's if, elif, and else working together.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 22, textAlign: "left" }}>
            {(testResults && testResults.length ? testResults : [
              { hunger: 95, actual: "ROAR!" },
              { hunger: 50, actual: "grumble" },
              { hunger: 10, actual: "zzz" },
            ]).map((r, i) => (
              <div key={i} style={{ background: "#0f172a", borderRadius: 10, padding: "10px 14px", border: "1px solid #059669" }}>
                <span style={{ fontFamily: "'Courier New', monospace", fontSize: 13, color: "#9CA3AF" }}>hunger = </span>
                <span style={{ fontFamily: "'Courier New', monospace", fontSize: 13, color: "#5EEAD4" }}>{r.hunger}</span>
                <span style={{ fontFamily: "'Courier New', monospace", fontSize: 13, color: "#6B7280" }}>{"  →  "}</span>
                <span style={{ fontFamily: "'Courier New', monospace", fontSize: 13, color: "#86EFAC" }}>{r.actual}</span>
              </div>
            ))}
          </div>

          <div style={{
            display: "inline-flex", alignItems: "center", gap: 12,
            background: "linear-gradient(135deg, #F0FDFA, #CCFBF1)",
            borderRadius: 14, padding: "14px 22px", marginBottom: 24, border: `1px solid ${color}40`,
          }}>
            <span style={{ fontSize: 28 }}>🐲</span>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontWeight: 800, color: "#0F766E", fontSize: 18 }}>+80 XP earned!</div>
              <div style={{ fontSize: 12, color: color }}>Dragon Whisperer badge unlocked 🏅</div>
            </div>
          </div>

          <div>
            <button onClick={handleComplete} style={{
              background: color, color: "#fff", border: "none", borderRadius: 14,
              padding: "14px 36px", cursor: "pointer", fontSize: 16, fontWeight: 800,
              boxShadow: `0 4px 20px ${color}40`,
            }}>Next Lesson →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── INNOVATORS LESSON (Ages 13–17) ───────────────────────────────────────────

const FN_PAIRS = [
  { input: '"Alice"', output: '"Hello, Alice!"' },
  { input: '"Bob"',   output: '"Hello, Bob!"'   },
  { input: '"World"', output: '"Hello, World!"' },
];

function InnovatorsLesson1({ onNav, progress, setProgress }) {
  const color = "#7C3AED";
  const [lessonPhase, setLessonPhase] = useState("watch");
  const [fnExplained, setFnExplained] = useState(false);
  const [animStep, setAnimStep]       = useState(-1);
  const [watchKey, setWatchKey]       = useState(0);
  const [code, setCode]               = useState("");
  const [runStatus, setRunStatus]     = useState(null); // null | "loading" | "running"
  const [testResults, setTestResults] = useState(null);
  const [failMsg, setFailMsg]         = useState(null);
  const [pyError, setPyError]         = useState(null);
  const [pyErrorType, setPyErrorType] = useState(null); // null | "syntax" | "other"
  const [refOpen, setRefOpen]         = useState(true);

  // Inject CSS
  useEffect(() => {
    const el = document.createElement("style");
    el.id = "cs-innovators-anim";
    el.textContent = `
      @keyframes csFnRowIn {
        0%   { opacity: 0; transform: translateY(8px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes csFnValueIn {
        0%   { opacity: 0; transform: scale(0.8); }
        60%  { opacity: 1; }
        100% { opacity: 1; transform: scale(1); }
      }
      @keyframes csFnThinkPulse {
        0%, 100% { opacity: 0.6; }
        50%      { opacity: 1; }
      }
    `;
    document.head.appendChild(el);
    return () => { const old = document.getElementById("cs-innovators-anim"); if (old) old.remove(); };
  }, []);

  // Animation sequencer — 3 rows × 3 steps each (input → thinking → output), then done
  // animStep: -1=idle, 0-2=row0, 3-5=row1, 6-8=row2, 9=done
  useEffect(() => {
    if (lessonPhase !== "watch" || !fnExplained) return;
    setAnimStep(-1);
    const timers = [];
    for (let s = 0; s <= 9; s++) {
      timers.push(setTimeout(() => setAnimStep(s), 400 + s * 1200));
    }
    return () => timers.forEach(clearTimeout);
  }, [lessonPhase, watchKey, fnExplained]);

  const runCode = async () => {
    setFailMsg(null);
    setPyError(null);
    setPyErrorType(null);
    setTestResults(null);
    setLessonPhase("check");
    setRunStatus("loading");
    let pyodide;
    try {
      pyodide = await getPyodide();
    } catch {
      setFailMsg("Couldn't load Python — check your connection and try again.");
      setRunStatus(null);
      setLessonPhase("think");
      return;
    }
    setRunStatus("running");
    try {
      pyodide.globals.set("_user_code", code);
      const result = await pyodide.runPythonAsync(`
import sys, io, json
_r1 = _r2 = None
_e1 = _e2 = None
_ce = None
try:
    exec(compile(_user_code, '<student>', 'exec'))
except Exception as e:
    _ce = type(e).__name__ + ': ' + str(e)
if _ce is None:
    try:
        _r1 = str(greet('World'))
    except Exception as e:
        _e1 = str(e)
    try:
        _r2 = str(greet('Alice'))
    except Exception as e:
        _e2 = str(e)
json.dumps([_r1, _e1, _r2, _e2, _ce])
      `);
      const [r1, e1, r2, e2, compileErr] = JSON.parse(result);
      if (compileErr) {
        const isSyntax = compileErr.startsWith("SyntaxError") || compileErr.startsWith("IndentationError") || compileErr.startsWith("TabError");
        setPyErrorType(isSyntax ? "syntax" : "other");
        setPyError(compileErr.split('\n').filter(l => l.trim()).slice(-2).join(' — '));
        setLessonPhase("think");
        setRunStatus(null);
        return;
      }
      const tests = [
        { name: "greet('World')", result: r1 !== null ? `'${r1}'` : `Error: ${e1}`, passed: r1 === "Hello, World!" },
        { name: "greet('Alice')", result: r2 !== null ? `'${r2}'` : `Error: ${e2}`, passed: r2 === "Hello, Alice!" },
      ];
      setTestResults(tests);
      if (tests.every(t => t.passed)) {
        setLessonPhase("celebrate");
      } else {
        setFailMsg("Your function ran! But the output wasn't quite right. Check the exact format: 'Hello, ' then the name then '!' — watch the comma and space carefully.");
        setLessonPhase("think");
      }
    } catch (err) {
      const msg = (err.message || "").split("\n").filter(l => l.trim()).slice(-2).join(" — ");
      setPyErrorType("other");
      setPyError(msg);
      setLessonPhase("think");
    }
    setRunStatus(null);
  };

  const handleComplete = () => {
    const updated = {
      ...progress,
      xp: progress.xp + 100,
      completedModules: [...new Set([...progress.completedModules, "teen-1"])],
    };
    setProgress(updated);
    saveProgress(updated);
    onNav("dashboard");
  };

  const isRunning = runStatus !== null;

  const iPhaseSteps = ["watch", "think", "check", "celebrate"];
  const handleBack = () => {
    const idx = iPhaseSteps.indexOf(lessonPhase);
    if (idx <= 0) { onNav("dashboard"); return; }
    const prev = iPhaseSteps[idx - 1];
    if (prev === "watch") { setLessonPhase("watch"); setFnExplained(false); setAnimStep(-1); }
    else { setLessonPhase(prev); }
  };

  return (
    <div style={{ maxWidth: 660, margin: "0 auto", padding: "24px 20px" }}>
      <button onClick={handleBack} style={{ background: "none", border: "none", cursor: "pointer", color: "#78716C", fontSize: 14, fontWeight: 600, marginBottom: 18 }}>← Back</button>

      <div style={{ background: "linear-gradient(135deg, #7C3AED, #6D28D9)", borderRadius: 20, padding: "22px 26px", marginBottom: 20, color: "#fff" }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>MODULE 1 · FUNCTIONS</div>
        <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Georgia', serif" }}>Functions</div>
      </div>

      <LessonProgress phase={lessonPhase} color={color} onStepClick={(step) => {
        if (step === "watch") {
          setLessonPhase("watch");
          setFnExplained(false);
          setAnimStep(-1);
        } else {
          setLessonPhase(step);
        }
      }} />

      {/* ── WATCH ── */}
      {lessonPhase === "watch" && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "20px", border: "1px solid #E7E5E4" }}>

          {/* ── EXPLAIN sub-phase ── */}
          {!fnExplained && (
            <>
              <div style={{ fontWeight: 800, color: "#44403C", fontSize: 16, marginBottom: 16 }}>
                Here's how a function is written in Python
              </div>

              {/* Syntax-coloured code block */}
              <pre style={{ background: "#1e1e1e", borderRadius: 10, padding: "16px 18px", marginBottom: 18, fontFamily: "'Courier New', monospace", fontSize: 14, lineHeight: 2.1, margin: "0 0 18px", overflowX: "auto" }}>
                <span style={{ color: "#569CD6" }}>def </span><span style={{ color: "#DCDCAA" }}>greet</span><span style={{ color: "#d4d4d4" }}>(</span><span style={{ color: "#9CDCFE" }}>name</span><span style={{ color: "#d4d4d4" }}>):</span>{"\n"}
                {"    "}<span style={{ color: "#C586C0" }}>return </span><span style={{ color: "#CE9178" }}>"Hello, " </span><span style={{ color: "#d4d4d4" }}>+ </span><span style={{ color: "#9CDCFE" }}>name </span><span style={{ color: "#d4d4d4" }}>+ </span><span style={{ color: "#CE9178" }}>"!"</span>{"\n"}
                {"\n"}
                <span style={{ color: "#9CDCFE" }}>result </span><span style={{ color: "#d4d4d4" }}>= </span><span style={{ color: "#DCDCAA" }}>greet</span><span style={{ color: "#d4d4d4" }}>(</span><span style={{ color: "#CE9178" }}>"Alice"</span><span style={{ color: "#d4d4d4" }}>)</span>{"\n"}
                <span style={{ color: "#DCDCAA" }}>print</span><span style={{ color: "#d4d4d4" }}>(</span><span style={{ color: "#9CDCFE" }}>result</span><span style={{ color: "#d4d4d4" }}>)</span>
              </pre>

              {/* Labeled callouts */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
                {/* Blue callout */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#EFF6FF", borderRadius: 10, padding: "12px 14px", border: "1px solid #BFDBFE" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#3B82F6", flexShrink: 0, marginTop: 3 }} />
                  <div style={{ fontSize: 13, color: "#1E3A5F", lineHeight: 1.6 }}>
                    <code style={{ background: "#DBEAFE", padding: "1px 6px", borderRadius: 4, color: "#1D4ED8", fontSize: 12 }}>def greet(name):</code>
                    {" "}— <strong>def</strong> means "define a function". <strong>greet</strong> is the name we give it. <strong>name</strong> is the input — called a <strong>parameter</strong>.
                  </div>
                </div>
                {/* Green callout */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#F0FDF4", borderRadius: 10, padding: "12px 14px", border: "1px solid #BBF7D0" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#22C55E", flexShrink: 0, marginTop: 3 }} />
                  <div style={{ fontSize: 13, color: "#14532D", lineHeight: 1.6 }}>
                    <code style={{ background: "#DCFCE7", padding: "1px 6px", borderRadius: 4, color: "#15803D", fontSize: 12 }}>{"    "}return ...</code>
                    {" "}— <strong>return</strong> sends the answer back. Everything inside the function must be <strong>indented</strong>.
                  </div>
                </div>
                {/* Purple callout */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#F5F3FF", borderRadius: 10, padding: "12px 14px", border: "1px solid #DDD6FE" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#8B5CF6", flexShrink: 0, marginTop: 3 }} />
                  <div style={{ fontSize: 13, color: "#3B0764", lineHeight: 1.6 }}>
                    <code style={{ background: "#EDE9FE", padding: "1px 6px", borderRadius: 4, color: "#6D28D9", fontSize: 12 }}>greet('Alice')</code>
                    {" "}— This <strong>calls</strong> the function and passes <code style={{ background: "#EDE9FE", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>'Alice'</code> as the input.
                  </div>
                </div>
                {/* Amber warning callout */}
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#FFFBEB", borderRadius: 10, padding: "12px 14px", border: "1px solid #F59E0B" }}>
                  <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#F59E0B", flexShrink: 0, marginTop: 3 }} />
                  <div style={{ fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
                    Just like loops, everything inside a function must be indented with <strong>4 spaces</strong>. The <code style={{ background: "#FEF3C7", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>def</code> line ends with a colon (<code style={{ background: "#FEF3C7", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>:</code>).
                  </div>
                </div>
              </div>

              <button onClick={() => setFnExplained(true)} style={{
                width: "100%", background: color, color: "#fff", border: "none", borderRadius: 12,
                padding: "13px", cursor: "pointer", fontSize: 14, fontWeight: 800,
                boxShadow: `0 4px 14px ${color}40`,
              }}>Got it, show me the machine! →</button>
            </>
          )}

          {/* ── ANIMATION sub-phase ── */}
          {fnExplained && (
            <>
              <div style={{ fontWeight: 800, color: "#44403C", fontSize: 15, marginBottom: 4 }}>🔧 Watch how a function works</div>
              <div style={{ fontSize: 13, color: "#78716C", marginBottom: 16 }}>
                The <strong>same function</strong>, different <strong>inputs</strong> → different <strong>outputs</strong>. Watch:
              </div>

              {/* Step-by-step rows */}
              <div style={{ background: "#0f172a", borderRadius: 14, padding: "18px 14px", marginBottom: 14, display: "flex", flexDirection: "column", gap: 16 }}>
                {FN_PAIRS.map((pair, r) => {
                  const rowBase   = r * 3;
                  const visible    = animStep >= rowBase;
                  const isThinking = animStep === rowBase + 1;
                  const hasOutput  = animStep >= rowBase + 2;
                  const isDim      = animStep >= rowBase + 3;
                  if (!visible) return null;
                  return (
                    <div key={r} style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 22px 108px 22px 1fr",
                      alignItems: "center",
                      gap: 4,
                      opacity: isDim ? 0.4 : 1,
                      transition: "opacity 0.7s",
                      animation: animStep === rowBase ? "csFnRowIn 0.5s ease-out" : "none",
                    }}>

                      {/* INPUT */}
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                        <div style={{ fontSize: 9, color: "#64748B", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>Input</div>
                        <div style={{
                          background: "#1e3a5f", color: "#93C5FD", borderRadius: 8, padding: "6px 8px",
                          fontFamily: "'Courier New', monospace", fontSize: 12, fontWeight: 700,
                          animation: animStep === rowBase ? "csFnValueIn 0.6s ease-out" : "none",
                          textAlign: "center", lineHeight: 1.4,
                        }}>{pair.input}</div>
                      </div>

                      {/* Arrow in */}
                      <div style={{ textAlign: "center", fontSize: 16, color: "#475569", paddingTop: 18 }}>→</div>

                      {/* Machine box */}
                      <div style={{
                        background: "#2e1065", borderRadius: 10,
                        border: `2px solid ${!isDim ? "#7C3AED" : "#3b1066"}`,
                        padding: "8px 6px", textAlign: "center", transition: "border-color 0.4s",
                      }}>
                        <div style={{ fontSize: 10, color: "#A78BFA", fontWeight: 800, letterSpacing: 0.3, marginBottom: 4, fontFamily: "'Courier New', monospace" }}>greet(name)</div>
                        {/* "Thinking" code — fades in/out */}
                        <div style={{
                          fontFamily: "'Courier New', monospace", fontSize: 9, color: "#C4B5FD",
                          lineHeight: 1.7, opacity: isThinking ? 1 : 0, transition: "opacity 0.35s",
                          animation: isThinking ? "csFnThinkPulse 0.8s ease-in-out infinite" : "none",
                        }}>
                          <div>return</div>
                          <div>"Hello, "</div>
                          <div>+ name + "!"</div>
                        </div>
                      </div>

                      {/* Arrow out */}
                      <div style={{ textAlign: "center", fontSize: 16, color: hasOutput ? "#10B981" : "#1e293b", paddingTop: 18, transition: "color 0.4s" }}>→</div>

                      {/* OUTPUT */}
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
                        <div style={{ fontSize: 9, color: "#64748B", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>Output</div>
                        {hasOutput ? (
                          <div style={{
                            background: "#0f2e1a", color: "#86EFAC", borderRadius: 8, padding: "6px 8px",
                            fontFamily: "'Courier New', monospace", fontSize: 11, fontWeight: 700,
                            animation: animStep === rowBase + 2 ? "csFnValueIn 0.5s ease-out" : "none",
                            textAlign: "center", lineHeight: 1.4, overflowWrap: "break-word", wordBreak: "break-word",
                          }}>{pair.output}</div>
                        ) : (
                          <div style={{ height: 32 }} />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {animStep === 9 && (
                <div style={{ background: "#F5F3FF", border: "1px solid #7C3AED30", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#5B21B6" }}>
                  Now write your own greet function. Use the example as a guide — but type it yourself. That's how it sticks!
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setWatchKey(k => k + 1)} style={{
                  background: "#F5F3FF", color, border: `1px solid ${color}40`,
                  borderRadius: 10, padding: "9px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600,
                }}>Watch again 🔄</button>
                {animStep === 9 && (
                  <button onClick={() => setLessonPhase("think")} style={{
                    flex: 1, background: color, color: "#fff", border: "none", borderRadius: 10,
                    padding: "9px", cursor: "pointer", fontSize: 13, fontWeight: 800,
                    boxShadow: `0 4px 14px ${color}40`,
                  }}>I understand functions! →</button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── THINK + CHECK ── */}
      {(lessonPhase === "think" || lessonPhase === "check") && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "20px", border: "1px solid #E7E5E4" }}>
          <div style={{ background: "#F5F3FF", borderRadius: 12, padding: "12px 16px", marginBottom: 16, border: "1px solid #7C3AED30" }}>
            <div style={{ fontWeight: 700, color: "#5B21B6", marginBottom: 4 }}>🧠 Brain First!</div>
            <div style={{ fontSize: 13, color: "#44403C", lineHeight: 1.6 }}>
              Write a function called <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>greet(name)</code> that <strong>returns</strong> <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>"Hello, [name]!"</code>. Fill in the <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>return</code> line in the skeleton below.
            </div>
          </div>

          {/* Collapsible syntax reference card */}
          <div style={{ marginBottom: 14, border: "1px solid #DDD6FE", borderRadius: 10, overflow: "hidden" }}>
            <button
              onClick={() => setRefOpen(o => !o)}
              style={{
                width: "100%", background: "#F5F3FF", border: "none", padding: "10px 14px",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#5B21B6",
              }}
            >
              <span>📋 Syntax reminder — click to {refOpen ? "hide" : "show"}</span>
              <span style={{ fontSize: 11, color: "#7C3AED" }}>{refOpen ? "▲" : "▼"}</span>
            </button>
            {refOpen && (
              <pre style={{ background: "#1e1e1e", padding: "12px 18px", fontFamily: "'Courier New', monospace", fontSize: 13, lineHeight: 2.1, margin: 0 }}><span style={{ color: "#569CD6" }}>def </span><span style={{ color: "#DCDCAA" }}>function_name</span><span style={{ color: "#d4d4d4" }}>(</span><span style={{ color: "#9CDCFE" }}>parameter</span><span style={{ color: "#d4d4d4" }}>):</span>{"\n"}{"    "}<span style={{ color: "#C586C0" }}>return </span><span style={{ color: "#9CDCFE" }}>something</span></pre>
            )}
          </div>

          <textarea
            value={code}
            onChange={e => {
              setCode(e.target.value);
              if (testResults) { setTestResults(null); setFailMsg(null); setPyError(null); setPyErrorType(null); }
            }}
            readOnly={isRunning}
            onKeyDown={e => {
              if (e.key === "Tab") {
                e.preventDefault();
                const s = e.target.selectionStart;
                const newVal = code.substring(0, s) + "    " + code.substring(e.target.selectionEnd);
                setCode(newVal);
                requestAnimationFrame(() => { e.target.selectionStart = e.target.selectionEnd = s + 4; });
              }
            }}
            placeholder={`def greet(name):\n    return "Hello, " + name + "!"\n\nprint(greet("World"))`}
            spellCheck={false}
            style={{
              width: "100%", boxSizing: "border-box",
              background: "#1e1e1e", color: "#d4d4d4",
              fontFamily: "'Courier New', Courier, monospace",
              fontSize: 14, padding: "16px", borderRadius: 10,
              minHeight: 160, lineHeight: 1.8,
              border: "2px solid #3e3e3e", outline: "none", resize: "vertical", marginBottom: 12,
            }}
          />

          {failMsg && (
            <div style={{ background: "#FEF3C7", border: "1px solid #F59E0B40", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#92400E" }}>
              ⚠️ {failMsg}
            </div>
          )}

          {pyError && pyErrorType === "syntax" && (() => {
            const defLine = code.split("\n").find(l => l.trim().startsWith("def "));
            const defMissingColon = defLine && !defLine.trim().endsWith(":");
            return (
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 12, fontSize: 13, color: "#7F1D1D", lineHeight: 1.7 }}>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>⚠️ Syntax issue</div>
                {defMissingColon
                  ? <>Your <code style={{ background: "#FEE2E2", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>def</code> line is missing a colon at the end — it must finish with <code style={{ background: "#FEE2E2", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>:</code></>
                  : <>Something about how the code is written isn't quite right. Check for missing brackets, quote marks, or indentation on the <code style={{ background: "#FEE2E2", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>return</code> line.</>
                }
              </div>
            );
          })()}

          {pyError && pyErrorType === "other" && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 12, fontSize: 13, color: "#7F1D1D", lineHeight: 1.7 }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>⚠️ Python found a problem in your code</div>
              <div style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: "#991B1B", background: "#FEE2E2", borderRadius: 6, padding: "6px 10px" }}>{pyError}</div>
            </div>
          )}

          {/* Test results on failure */}
          {testResults && lessonPhase === "think" && (
            <div style={{ background: "#0f172a", borderRadius: 10, border: "1px solid #1e293b", overflow: "hidden", marginBottom: 12 }}>
              <div style={{ padding: "7px 14px", background: "#1e293b", fontSize: 10, fontWeight: 700, color: "#6B7280", letterSpacing: 1 }}>TEST RESULTS</div>
              {testResults.map((test, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderTop: i > 0 ? "1px solid #1e293b" : "none" }}>
                  <div style={{ fontFamily: "monospace", fontSize: 12, color: "#d4d4d4", flexShrink: 0 }}>{test.name}</div>
                  <div style={{ fontFamily: "monospace", fontSize: 12, color: test.passed ? "#86EFAC" : "#FCD34D", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>→ {test.result}</div>
                  <div style={{ fontWeight: 800, fontSize: 12, color: test.passed ? "#4ADE80" : "#F59E0B", flexShrink: 0 }}>
                    {test.passed ? "PASSED ✓" : "FAILED ✗"}
                  </div>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={runCode}
            disabled={code.length < 30 || isRunning}
            style={{
              width: "100%", border: "none", borderRadius: 12, padding: "13px",
              cursor: code.length < 30 || isRunning ? "default" : "pointer",
              fontSize: 14, fontWeight: 800, transition: "all 0.15s",
              background: isRunning ? `${color}99` : code.length < 30 ? "#E7E5E4" : color,
              color: code.length < 30 && !isRunning ? "#A8A29E" : "#fff",
            }}
          >
            {runStatus === "loading" ? "Loading Python... 🐍" : runStatus === "running" ? "Running tests..." : code.length < 30 ? "✍️ Write at least 30 characters first..." : "Run my code →"}
          </button>
        </div>
      )}

      {/* ── CELEBRATE ── */}
      {lessonPhase === "celebrate" && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "24px", border: "1px solid #E7E5E4", textAlign: "center" }}>
          <div style={{ fontSize: 52, marginBottom: 10 }}>🧪✨</div>
          <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 24, fontWeight: 900, color: "#44403C", margin: "0 0 8px" }}>
            Your function passed all tests!
          </h2>
          <p style={{ fontSize: 14, color: "#78716C", lineHeight: 1.7, marginBottom: 20 }}>
            You wrote a reusable function — feed it any name, it always returns the right output. That's the core idea behind all software.
          </p>

          {/* Test output block */}
          <div style={{ background: "#0f172a", borderRadius: 12, border: "1px solid #1e293b", overflow: "hidden", marginBottom: 22, textAlign: "left" }}>
            <div style={{ padding: "8px 16px", background: "#1e293b", fontSize: 10, fontWeight: 700, color: "#6B7280", letterSpacing: 1, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: "#4ADE80", fontSize: 12 }}>●</span> TEST RESULTS — ALL PASSED
            </div>
            {(testResults || []).map((test, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 16px", borderTop: i > 0 ? "1px solid #1e293b" : "none" }}>
                <div style={{ fontFamily: "monospace", fontSize: 13, color: "#A78BFA", flexShrink: 0 }}>{test.name}</div>
                <div style={{ color: "#374151", flexShrink: 0 }}>→</div>
                <div style={{ fontFamily: "monospace", fontSize: 13, color: "#86EFAC", flex: 1 }}>{test.result}</div>
                <div style={{ fontWeight: 800, fontSize: 12, color: "#4ADE80", flexShrink: 0 }}>PASSED ✓</div>
              </div>
            ))}
          </div>

          <div style={{
            display: "inline-flex", alignItems: "center", gap: 12,
            background: "linear-gradient(135deg, #F5F3FF, #EDE9FE)",
            borderRadius: 14, padding: "14px 22px", marginBottom: 24, border: `1px solid ${color}40`,
          }}>
            <span style={{ fontSize: 28 }}>💡</span>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontWeight: 800, color: "#5B21B6", fontSize: 18 }}>+100 XP earned!</div>
              <div style={{ fontSize: 12, color }}>Function Builder badge unlocked 🏅</div>
            </div>
          </div>

          <div>
            <button onClick={handleComplete} style={{
              background: color, color: "#fff", border: "none", borderRadius: 14,
              padding: "14px 36px", cursor: "pointer", fontSize: 16, fontWeight: 800,
              boxShadow: `0 4px 20px ${color}40`,
            }}>Next Lesson →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── INNOVATORS LESSON 2 — Lists and Loops (Greet the Crew) ───────────────────

function InnovatorsLesson2({ onNav, progress, setProgress }) {
  const color = "#7C3AED";
  const [lessonPhase, setLessonPhase] = useState("watch"); // watch | think | check | celebrate
  const [explainStep, setExplainStep] = useState(0); // 0,1,2 = explain; 3 = animation
  const [animStep, setAnimStep]       = useState(-1); // -1 waiting, 0-2 iterating, 3 done
  const [watchKey, setWatchKey]       = useState(0);

  // Think / Check phase
  const [code, setCode]               = useState("");
  const [runStatus, setRunStatus]     = useState(null); // null | "loading" | "running"
  const [testResults, setTestResults] = useState(null);
  const [failMsg, setFailMsg]         = useState(null);
  const [pyError, setPyError]         = useState(null);
  const [pyErrorType, setPyErrorType] = useState(null); // null | "indent" | "syntax" | "other"

  const demoCrew = ["Ash", "Jun", "Mia"];

  // Inject animations
  useEffect(() => {
    const el = document.createElement("style");
    el.id = "cs-innov2-anim";
    el.textContent = `
      @keyframes csCrewRowIn {
        0%   { opacity: 0; transform: translateY(6px); }
        100% { opacity: 1; transform: translateY(0); }
      }
      @keyframes csCrewHighlight {
        0%   { background: rgba(124,58,237,0.55); }
        100% { background: rgba(124,58,237,0.30); }
      }
      @keyframes csHelloIn {
        0%   { opacity: 0; transform: translateX(-6px); }
        100% { opacity: 1; transform: translateX(0); }
      }
    `;
    document.head.appendChild(el);
    return () => { const old = document.getElementById("cs-innov2-anim"); if (old) old.remove(); };
  }, []);

  // Animation driver — only runs when user clicks "Watch it run"
  useEffect(() => {
    if (lessonPhase !== "watch" || explainStep !== 3) return;
    setAnimStep(-1);
    let intervalId = null;
    const startTimer = setTimeout(() => {
      setAnimStep(0);
      let i = 1;
      intervalId = setInterval(() => {
        if (i <= 2) {
          setAnimStep(i);
          i++;
        } else {
          clearInterval(intervalId);
          intervalId = null;
          setAnimStep(3);
        }
      }, 1100);
    }, 400);
    return () => {
      clearTimeout(startTimer);
      if (intervalId) clearInterval(intervalId);
    };
  }, [lessonPhase, explainStep, watchKey]);

  const runCode = async () => {
    setFailMsg(null);
    setPyError(null);
    setPyErrorType(null);
    setTestResults(null);

    // Pre-check: student has redefined `crew` — this overrides our per-test injection
    const crewRedefPattern = /^\s*crew\s*=/m;
    if (crewRedefPattern.test(code)) {
      setFailMsg("Looks like you wrote `crew = [...]` in your code. Remove that line - we set `crew` for each test. Just write the `for` loop.");
      return;
    }

    setLessonPhase("check");
    setRunStatus("loading");

    let pyodide;
    try {
      pyodide = await getPyodide();
    } catch {
      setFailMsg("Couldn't load Python - check your connection and try again.");
      setRunStatus(null);
      setLessonPhase("think");
      return;
    }
    setRunStatus("running");

    const testCases = [
      { crew: ["Alice", "Bob", "Carol"],    expected: ["Hello, Alice!", "Hello, Bob!", "Hello, Carol!"] },
      { crew: ["Solo"],                     expected: ["Hello, Solo!"] },
      { crew: ["Ash", "Jun", "Mia", "Leo"], expected: ["Hello, Ash!", "Hello, Jun!", "Hello, Mia!", "Hello, Leo!"] },
    ];

    const results = [];
    let caughtError = false;

    for (const tc of testCases) {
      try {
        pyodide.globals.set("_user_code", code);
        pyodide.globals.set("crew", pyodide.toPy(tc.crew));
        const output = await pyodide.runPythonAsync(`
import sys, io
_buf = io.StringIO()
sys.stdout = _buf
try:
    exec(compile(_user_code, '<student>', 'exec'))
finally:
    sys.stdout = sys.__stdout__
_buf.getvalue()
        `);
        const actualLines = output.split("\n").filter(l => l.trim() !== "");
        const pass =
          actualLines.length === tc.expected.length &&
          actualLines.every((l, i) => l === tc.expected[i]);
        results.push({ crew: tc.crew, expected: tc.expected, actual: actualLines, pass });
      } catch (err) {
        const fullMsg = err.message || "";
        const technical = fullMsg.split("\n").filter(l => l.trim()).slice(-2).join(" - ");
        let errorType = "other";
        if (fullMsg.includes("IndentationError")) errorType = "indent";
        else if (fullMsg.includes("SyntaxError")) errorType = "syntax";
        setPyErrorType(errorType);
        setPyError(technical);
        setLessonPhase("think");
        caughtError = true;
        break;
      }
    }

    if (!caughtError) {
      setTestResults(results);
      const allPass = results.every(r => r.pass);
      if (allPass) {
        setLessonPhase("celebrate");
      } else {
        setFailMsg("Your code ran, but the output wasn't quite right for every crew. Check the results below - watch the exact greeting format.");
        setLessonPhase("think");
      }
    }
    setRunStatus(null);
  };

  const handleComplete = () => {
    const updated = {
      ...progress,
      xp: progress.xp + 120,
      completedModules: [...new Set([...progress.completedModules, "teen-2"])],
    };
    setProgress(updated);
    saveProgress(updated);
    onNav("dashboard");
  };

  const isRunning = runStatus !== null;

  const iPhaseSteps = ["watch", "think", "check", "celebrate"];
  const handleBack = () => {
    if (lessonPhase === "watch" && explainStep > 0) {
      setExplainStep(s => s - 1);
      return;
    }
    const idx = iPhaseSteps.indexOf(lessonPhase);
    if (idx <= 0) { onNav("dashboard"); return; }
    const prev = iPhaseSteps[idx - 1];
    if (prev === "watch") { setLessonPhase("watch"); setExplainStep(0); setAnimStep(-1); }
    else { setLessonPhase(prev); }
  };

  return (
    <div style={{ maxWidth: 660, margin: "0 auto", padding: "24px 20px" }}>
      <button onClick={handleBack} style={{ background: "none", border: "none", cursor: "pointer", color: "#78716C", fontSize: 14, fontWeight: 600, marginBottom: 18 }}>← Back</button>

      <div style={{ background: "linear-gradient(135deg, #7C3AED, #5B21B6)", borderRadius: 20, padding: "22px 26px", marginBottom: 20, color: "#fff" }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>MODULE 2 · LISTS AND LOOPS</div>
        <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Georgia', serif" }}>Greet the Crew 👋</div>
      </div>

      <LessonProgress phase={lessonPhase} color={color} onStepClick={(step) => {
        if (step === "watch") {
          setLessonPhase("watch");
          setExplainStep(0);
          setAnimStep(-1);
        } else {
          setLessonPhase(step);
        }
      }} />

      {/* ── WATCH ── */}
      {lessonPhase === "watch" && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "20px", border: "1px solid #E7E5E4" }}>

          {/* ── EXPLAIN sub-phase: three screens ── */}
          {explainStep < 3 && (
            <>
              <div style={{ fontSize: 11, color: "#7C3AED", letterSpacing: 1, fontWeight: 700, textTransform: "uppercase", marginBottom: 10 }}>
                Step {explainStep + 1} of 3
              </div>

              {/* Screen 1 — What's a list? */}
              {explainStep === 0 && (
                <>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#44403C", marginBottom: 14 }}>
                    First, what is a list?
                  </div>
                  <p style={{ fontSize: 14, color: "#44403C", lineHeight: 1.7, margin: "0 0 16px" }}>
                    A list holds several things in order, under one name. You make one with square brackets and commas between each item.
                  </p>
                  <pre style={{ background: "#1e1e1e", borderRadius: 10, padding: "16px 18px", marginBottom: 16, fontFamily: "'Courier New', monospace", fontSize: 14, lineHeight: 2, margin: "0 0 16px", overflowX: "auto" }}>
                    <span style={{ color: "#9CDCFE" }}>crew </span>
                    <span style={{ color: "#d4d4d4" }}>= </span>
                    <span style={{ color: "#d4d4d4" }}>[</span>
                    <span style={{ color: "#CE9178" }}>"Ash"</span>
                    <span style={{ color: "#d4d4d4" }}>, </span>
                    <span style={{ color: "#CE9178" }}>"Jun"</span>
                    <span style={{ color: "#d4d4d4" }}>, </span>
                    <span style={{ color: "#CE9178" }}>"Mia"</span>
                    <span style={{ color: "#d4d4d4" }}>]</span>
                  </pre>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#F5F3FF", borderRadius: 10, padding: "12px 14px", border: "1px solid #DDD6FE", marginBottom: 18 }}>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#7C3AED", flexShrink: 0, marginTop: 3 }} />
                    <div style={{ fontSize: 13, color: "#3B0764", lineHeight: 1.6 }}>
                      Each item is called an "element". This crew has 3 elements. The order matters - <code style={{ background: "#EDE9FE", padding: "1px 5px", borderRadius: 4, color: "#6D28D9", fontSize: 12 }}>"Ash"</code> comes first, <code style={{ background: "#EDE9FE", padding: "1px 5px", borderRadius: 4, color: "#6D28D9", fontSize: 12 }}>"Mia"</code> comes last.
                    </div>
                  </div>
                </>
              )}

              {/* Screen 2 — How the loop works */}
              {explainStep === 1 && (
                <>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#44403C", marginBottom: 14 }}>
                    Now, what does the loop actually do?
                  </div>
                  <pre style={{ background: "#1e1e1e", borderRadius: 10, padding: "16px 18px", marginBottom: 16, fontFamily: "'Courier New', monospace", fontSize: 14, lineHeight: 2, margin: "0 0 16px", overflowX: "auto" }}>
                    <span style={{ color: "#569CD6" }}>for </span>
                    <span style={{ color: "#9CDCFE" }}>name </span>
                    <span style={{ color: "#569CD6" }}>in </span>
                    <span style={{ color: "#9CDCFE" }}>crew</span>
                    <span style={{ color: "#d4d4d4" }}>:</span>{"\n"}
                    {"    "}
                    <span style={{ color: "#DCDCAA" }}>print</span>
                    <span style={{ color: "#d4d4d4" }}>(</span>
                    <span style={{ color: "#CE9178" }}>f"Hello, </span>
                    <span style={{ color: "#d4d4d4" }}>{"{"}</span>
                    <span style={{ color: "#9CDCFE" }}>name</span>
                    <span style={{ color: "#d4d4d4" }}>{"}"}</span>
                    <span style={{ color: "#CE9178" }}>!"</span>
                    <span style={{ color: "#d4d4d4" }}>)</span>
                  </pre>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 18 }}>
                    {/* Blue */}
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#EFF6FF", borderRadius: 10, padding: "12px 14px", border: "1px solid #BFDBFE" }}>
                      <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#3B82F6", flexShrink: 0, marginTop: 3 }} />
                      <div style={{ fontSize: 13, color: "#1E3A5F", lineHeight: 1.6 }}>
                        <code style={{ background: "#DBEAFE", padding: "1px 6px", borderRadius: 4, color: "#1D4ED8", fontSize: 12 }}>for</code>
                        {" "}- the keyword that starts a loop in Python.
                      </div>
                    </div>
                    {/* Green */}
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#F0FDF4", borderRadius: 10, padding: "12px 14px", border: "1px solid #BBF7D0" }}>
                      <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#22C55E", flexShrink: 0, marginTop: 3 }} />
                      <div style={{ fontSize: 13, color: "#14532D", lineHeight: 1.6 }}>
                        <code style={{ background: "#DCFCE7", padding: "1px 6px", borderRadius: 4, color: "#15803D", fontSize: 12 }}>name</code>
                        {" "}- a variable YOU name. It holds each crew member one at a time. Could be called anything - <code style={{ background: "#DCFCE7", padding: "1px 5px", borderRadius: 4, color: "#15803D", fontSize: 12 }}>name</code>, <code style={{ background: "#DCFCE7", padding: "1px 5px", borderRadius: 4, color: "#15803D", fontSize: 12 }}>person</code>, even <code style={{ background: "#DCFCE7", padding: "1px 5px", borderRadius: 4, color: "#15803D", fontSize: 12 }}>x</code>. Pick something that describes what it holds.
                      </div>
                    </div>
                    {/* Purple */}
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#F5F3FF", borderRadius: 10, padding: "12px 14px", border: "1px solid #DDD6FE" }}>
                      <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#7C3AED", flexShrink: 0, marginTop: 3 }} />
                      <div style={{ fontSize: 13, color: "#3B0764", lineHeight: 1.6 }}>
                        <code style={{ background: "#EDE9FE", padding: "1px 6px", borderRadius: 4, color: "#6D28D9", fontSize: 12 }}>in crew:</code>
                        {" "}- the list being stepped through. The colon means "what comes next runs once per item."
                      </div>
                    </div>
                    {/* Amber */}
                    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#FFFBEB", borderRadius: 10, padding: "12px 14px", border: "1px solid #FCD34D" }}>
                      <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#F59E0B", flexShrink: 0, marginTop: 3 }} />
                      <div style={{ fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>
                        The line below must be indented by <strong>4 spaces</strong> (or one Tab). This tells Python "this line is inside the loop - run it each time."
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Screen 3 — What happens each time */}
              {explainStep === 2 && (
                <>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#44403C", marginBottom: 14 }}>
                    Here's the mental model
                  </div>
                  <p style={{ fontSize: 14, color: "#44403C", lineHeight: 1.7, margin: "0 0 14px" }}>
                    Every time the loop runs, <code style={{ background: "#F5F3FF", padding: "1px 5px", borderRadius: 4, color: "#6D28D9", fontSize: 13 }}>name</code> takes the next value from the list, and the indented line runs with that value.
                  </p>
                  <div style={{ marginBottom: 14 }}>
                    {[
                      { label: "Iteration 1", state: 'name = "Ash"', result: "Hello, Ash!" },
                      { label: "Iteration 2", state: 'name = "Jun"', result: "Hello, Jun!" },
                      { label: "Iteration 3", state: 'name = "Mia"', result: "Hello, Mia!" },
                    ].map((row, i) => (
                      <div key={i} style={{
                        background: "#0f172a", borderRadius: 10, padding: "10px 14px", marginBottom: 8,
                        border: "1px solid #1e293b",
                        display: "grid", gridTemplateColumns: "110px 1fr 22px 1fr", alignItems: "center", gap: 8,
                      }}>
                        <div style={{ fontSize: 11, color: "#A78BFA", letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>{row.label}</div>
                        <div style={{ fontFamily: "'Courier New', monospace", fontSize: 13, color: "#9CDCFE" }}>{row.state}</div>
                        <div style={{ color: "#6B7280", fontSize: 14, textAlign: "center" }}>→</div>
                        <div style={{ fontFamily: "'Courier New', monospace", fontSize: 13, color: "#86EFAC" }}>{row.result}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: "#F5F3FF", borderRadius: 10, padding: "12px 14px", border: "1px solid #DDD6FE", marginBottom: 18 }}>
                    <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#7C3AED", flexShrink: 0, marginTop: 3 }} />
                    <div style={{ fontSize: 13, color: "#3B0764", lineHeight: 1.6 }}>
                      One loop, three iterations, three greetings. Now watch it run live.
                    </div>
                  </div>
                </>
              )}

              {/* Nav row */}
              <div style={{ display: "flex", gap: 8 }}>
                {explainStep > 0 && (
                  <button onClick={() => setExplainStep(s => s - 1)} style={{
                    background: "#F5F3FF", color, border: `1px solid ${color}40`,
                    borderRadius: 10, padding: "11px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600,
                  }}>← Back</button>
                )}
                <button onClick={() => setExplainStep(s => s + 1)} style={{
                  flex: 1, background: color, color: "#fff", border: "none", borderRadius: 10,
                  padding: "11px", cursor: "pointer", fontSize: 13, fontWeight: 800,
                  boxShadow: `0 4px 14px ${color}40`,
                }}>
                  {explainStep === 0 ? "Next: what's a loop? →" : explainStep === 1 ? "Next: what happens each time? →" : "Watch it run →"}
                </button>
              </div>
            </>
          )}

          {/* ── ANIMATION sub-phase ── */}
          {explainStep === 3 && (
            <>
              <div style={{ fontWeight: 800, color: "#44403C", fontSize: 15, marginBottom: 4 }}>👀 Watch the loop step through the crew</div>
              <div style={{ fontSize: 13, color: "#78716C", marginBottom: 14 }}>
                Each name gets its own greeting - one loop, three outputs.
              </div>

              <pre style={{ background: "#1e1e1e", borderRadius: 10, padding: "14px 16px", marginBottom: 14, fontFamily: "'Courier New', monospace", fontSize: 13, lineHeight: 2, margin: "0 0 14px", overflowX: "auto" }}>
                <span style={{ color: "#9CDCFE" }}>crew </span>
                <span style={{ color: "#d4d4d4" }}>= [</span>
                <span style={{ color: "#CE9178" }}>"Ash"</span>
                <span style={{ color: "#d4d4d4" }}>, </span>
                <span style={{ color: "#CE9178" }}>"Jun"</span>
                <span style={{ color: "#d4d4d4" }}>, </span>
                <span style={{ color: "#CE9178" }}>"Mia"</span>
                <span style={{ color: "#d4d4d4" }}>]</span>{"\n"}
                <span style={{ color: "#569CD6" }}>for </span>
                <span style={{ color: "#9CDCFE" }}>name </span>
                <span style={{ color: "#569CD6" }}>in </span>
                <span style={{ color: "#9CDCFE" }}>crew</span>
                <span style={{ color: "#d4d4d4" }}>:</span>{"\n"}
                {"    "}
                <span style={{ color: "#DCDCAA" }}>print</span>
                <span style={{ color: "#d4d4d4" }}>(</span>
                <span style={{ color: "#CE9178" }}>f"Hello, </span>
                <span style={{ color: "#d4d4d4" }}>{"{"}</span>
                <span style={{ color: "#9CDCFE" }}>name</span>
                <span style={{ color: "#d4d4d4" }}>{"}"}</span>
                <span style={{ color: "#CE9178" }}>!"</span>
                <span style={{ color: "#d4d4d4" }}>)</span>
              </pre>

              {/* Crew + Output */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                {/* Left: CREW */}
                <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px", border: "1px solid #7C3AED40" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#7C3AED", letterSpacing: 1, marginBottom: 10 }}>CREW</div>
                  {demoCrew.map((n, i) => {
                    const isActive = animStep === i;
                    const dim = animStep === -1 || animStep === 3;
                    return (
                      <div key={`${watchKey}-${i}-${isActive ? "on" : "off"}`} style={{
                        background: isActive ? "rgba(124,58,237,0.30)" : "#1e293b",
                        borderRadius: 8, padding: "8px 12px", marginBottom: 6,
                        fontFamily: "'Courier New', monospace", fontSize: 13, color: "#E9D5FF",
                        opacity: dim ? 0.5 : 1,
                        animation: isActive ? "csCrewHighlight 0.5s ease-out" : "none",
                        transition: "opacity 0.4s",
                      }}>"{n}"</div>
                    );
                  })}
                  <div style={{ marginTop: 8 }}>
                    {animStep === -1 && <div style={{ fontFamily: "monospace", fontSize: 12, color: "#374151", textAlign: "center" }}>waiting...</div>}
                    {animStep >= 0 && animStep < 3 && (
                      <div key={`state-${animStep}`} style={{ fontFamily: "'Courier New', monospace", fontSize: 13, color: "#5EEAD4", textAlign: "center", animation: "csCrewRowIn 0.25s ease-out" }}>
                        name = "{demoCrew[animStep]}"
                      </div>
                    )}
                    {animStep === 3 && <div style={{ fontFamily: "monospace", fontSize: 12, color: "#6EE7B7", textAlign: "center" }}>loop done ✓</div>}
                  </div>
                </div>

                {/* Right: OUTPUT */}
                <div style={{ background: "#0f172a", borderRadius: 10, padding: "14px", border: "1px solid #1e293b", minHeight: 110 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", letterSpacing: 1, marginBottom: 10 }}>OUTPUT</div>
                  {animStep === -1 && <div style={{ fontFamily: "monospace", fontSize: 13, color: "#374151" }}>...</div>}
                  {demoCrew.slice(0, animStep === 3 ? 3 : Math.max(0, animStep + 1)).map((n, i) => {
                    const isNewest = animStep < 3 && i === animStep;
                    return (
                      <div key={`out-${watchKey}-${i}`} style={{
                        fontFamily: "'Courier New', monospace", fontSize: 13, color: "#86EFAC", lineHeight: 1.7,
                        animation: isNewest ? "csHelloIn 0.25s ease-out" : "none",
                      }}>Hello, {n}!</div>
                    );
                  })}
                </div>
              </div>

              {animStep === 3 && (
                <div style={{ background: "#F5F3FF", border: "1px solid #7C3AED30", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#5B21B6" }}>
                  Three iterations, three greetings. Now write your own →
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setWatchKey(k => k + 1)} style={{
                  background: "#F5F3FF", color, border: `1px solid ${color}40`,
                  borderRadius: 10, padding: "9px 18px", cursor: "pointer", fontSize: 13, fontWeight: 600,
                }}>Watch again 🔄</button>
                {animStep === 3 && (
                  <button onClick={() => setLessonPhase("think")} style={{
                    flex: 1, background: color, color: "#fff", border: "none", borderRadius: 10,
                    padding: "9px", cursor: "pointer", fontSize: 13, fontWeight: 800,
                    boxShadow: `0 4px 14px ${color}40`,
                  }}>I get it, let's try! →</button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── THINK + CHECK ── */}
      {(lessonPhase === "think" || lessonPhase === "check") && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "20px", border: "1px solid #E7E5E4" }}>
          <div style={{
            background: "#F5F3FF",
            border: "1px solid #7C3AED40",
            borderRadius: 12,
            padding: "14px 16px",
            marginBottom: 14,
            display: "flex",
            gap: 12,
            alignItems: "flex-start",
          }}>
            <span style={{ fontSize: 20, flexShrink: 0 }}>⚙️</span>
            <div style={{ fontSize: 13, color: "#44403C", lineHeight: 1.6 }}>
              <div style={{ fontWeight: 700, color: "#5B21B6", marginBottom: 4 }}>Setup (already done for you)</div>
              We inject a different <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>crew</code> list for each test. Your job is just the loop - do NOT write <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>crew = [...]</code> in your code. Start your first line with <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>for</code>.
            </div>
          </div>

          <div style={{ background: "#F5F3FF", borderRadius: 12, padding: "12px 16px", marginBottom: 14, border: "1px solid #7C3AED30" }}>
            <div style={{ fontWeight: 700, color: "#5B21B6", marginBottom: 4 }}>🧠 Brain First!</div>
            <div style={{ fontSize: 13, color: "#44403C", lineHeight: 1.6 }}>
              Write a for loop that greets every member of a <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>crew</code> list. The <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>crew</code> variable is already set for you - don't redefine it. Each greeting should be exactly: <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>Hello, NAME!</code>
            </div>
          </div>

          <div style={{ background: "#FAFAF9", borderRadius: 10, padding: "12px 14px", border: "1px solid #E7E5E4", marginBottom: 12, fontSize: 13, color: "#44403C", lineHeight: 1.9 }}>
            <div style={{ fontWeight: 700, color: "#44403C", marginBottom: 8, fontSize: 13 }}>Expected behaviour</div>
            <div style={{ fontSize: 13, color: "#44403C", lineHeight: 1.7, marginBottom: 10 }}>
              For each name in <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>crew</code>, print: <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>Hello, NAME!</code>
            </div>
            <div style={{ background: "#F5F3FF", borderRadius: 8, padding: "10px 12px", border: "1px solid #DDD6FE", fontSize: 12, color: "#5B21B6", lineHeight: 1.7 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>When a test runs:</div>
              We might set <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4, fontSize: 11 }}>crew = ["Alice", "Bob"]</code> - your loop should output:
              <div style={{ fontFamily: "'Courier New', monospace", marginTop: 6, color: "#7C3AED" }}>
                Hello, Alice!<br />
                Hello, Bob!
              </div>
            </div>
          </div>

          <textarea
            value={code}
            onChange={e => setCode(e.target.value)}
            readOnly={isRunning}
            onKeyDown={e => {
              if (e.key === "Tab") {
                e.preventDefault();
                const s = e.target.selectionStart;
                const newVal = code.substring(0, s) + "    " + code.substring(e.target.selectionEnd);
                setCode(newVal);
                requestAnimationFrame(() => { e.target.selectionStart = e.target.selectionEnd = s + 4; });
              }
            }}
            placeholder="# Loop through crew and greet each one"
            spellCheck={false}
            style={{
              width: "100%", boxSizing: "border-box",
              background: "#1e1e1e", color: "#d4d4d4",
              fontFamily: "'Courier New', Courier, monospace",
              fontSize: 14, padding: "16px", borderRadius: 10,
              minHeight: 140, lineHeight: 1.8,
              border: "2px solid #3e3e3e", outline: "none", resize: "vertical",
              marginBottom: 12,
            }}
          />

          {failMsg && (
            <div style={{ background: "#FEF3C7", border: "1px solid #F59E0B40", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: "#92400E" }}>
              ⚠️ {failMsg}
            </div>
          )}

          {pyError && pyErrorType === "indent" && (
            <>
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 10, fontSize: 13, color: "#7F1D1D", lineHeight: 1.7 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>⚠️ Indentation Error</div>
                Python can't find the indented block. After the <code style={{ background: "#FEE2E2", padding: "1px 5px", borderRadius: 4, fontSize: 12 }}>for ... :</code> line, the next line must have <strong>4 spaces or a Tab</strong>.
              </div>
              <div style={{ background: "#1e1e1e", borderRadius: 8, padding: "12px 16px", marginBottom: 12, fontFamily: "'Courier New', monospace", fontSize: 13, lineHeight: 2 }}>
                <div>
                  <span style={{ color: "#569CD6" }}>for </span>
                  <span style={{ color: "#9CDCFE" }}>name </span>
                  <span style={{ color: "#569CD6" }}>in </span>
                  <span style={{ color: "#9CDCFE" }}>crew</span>
                  <span style={{ color: "#d4d4d4" }}>:</span>
                </div>
                <div>
                  <span style={{ color: "#d4d4d4" }}>{"    "}</span>
                  <span style={{ color: "#DCDCAA" }}>print</span>
                  <span style={{ color: "#d4d4d4" }}>(</span>
                  <span style={{ color: "#CE9178" }}>f'Hello, </span>
                  <span style={{ color: "#d4d4d4" }}>{"{"}</span>
                  <span style={{ color: "#9CDCFE" }}>name</span>
                  <span style={{ color: "#d4d4d4" }}>{"}"}</span>
                  <span style={{ color: "#CE9178" }}>!'</span>
                  <span style={{ color: "#d4d4d4" }}>)</span>
                  <span style={{ color: "#6A9955", marginLeft: 12 }}>← 4 spaces here</span>
                </div>
              </div>
            </>
          )}

          {pyError && pyErrorType === "syntax" && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 12, fontSize: 13, color: "#7F1D1D", lineHeight: 1.7 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>⚠️ Syntax Error</div>
              Something about how the code is written isn't quite right. Check for missing <strong>colons</strong>, <strong>brackets</strong>, or <strong>quote marks</strong>.
            </div>
          )}

          {pyError && pyErrorType === "other" && (
            <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, padding: "12px 16px", marginBottom: 12, fontSize: 13, color: "#7F1D1D", lineHeight: 1.7 }}>
              <div style={{ fontWeight: 800, marginBottom: 6 }}>⚠️ Python found a problem in your code</div>
              <div style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: "#991B1B", background: "#FEE2E2", borderRadius: 6, padding: "6px 10px" }}>{pyError}</div>
            </div>
          )}

          {failMsg && testResults && (
            <div style={{ background: "#0f172a", borderRadius: 10, padding: "12px", marginBottom: 12, border: "1px solid #374151" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#6B7280", letterSpacing: 1, marginBottom: 8 }}>TEST RESULTS</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {testResults.map((r, i) => (
                  <div key={i} style={{
                    background: "#1e293b", borderRadius: 8, padding: "10px 12px",
                    border: `1px solid ${r.pass ? "#7C3AED40" : "#F59E0B40"}`,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                      <div style={{ flex: 1, fontFamily: "'Courier New', monospace", fontSize: 12, color: "#9CDCFE", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>crew = {JSON.stringify(r.crew)}</div>
                      <div style={{ fontSize: 14, fontWeight: 900, color: r.pass ? "#4ADE80" : "#FCD34D", flexShrink: 0 }}>{r.pass ? "✓" : "✗"}</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: "#5EEAD4", letterSpacing: 1, marginBottom: 4 }}>EXPECTED</div>
                        {r.expected.map((l, j) => <div key={j} style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: "#5EEAD4", lineHeight: 1.6 }}>{l}</div>)}
                      </div>
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: r.pass ? "#86EFAC" : "#FCD34D", letterSpacing: 1, marginBottom: 4 }}>YOUR OUTPUT</div>
                        {r.actual.length === 0 && <div style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: "#6B7280" }}>(no output)</div>}
                        {r.actual.map((l, j) => <div key={j} style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: r.pass ? "#86EFAC" : "#FCD34D", lineHeight: 1.6 }}>{l}</div>)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={runCode}
            disabled={code.length < 20 || isRunning}
            style={{
              width: "100%", border: "none", borderRadius: 12, padding: "13px",
              cursor: code.length < 20 || isRunning ? "default" : "pointer",
              fontSize: 14, fontWeight: 800, transition: "all 0.15s",
              background: isRunning ? `${color}99` : code.length < 20 ? "#E7E5E4" : color,
              color: code.length < 20 && !isRunning ? "#A8A29E" : "#fff",
            }}
          >
            {runStatus === "loading" ? "Loading Python... 🐍" : runStatus === "running" ? "Running your code..." : code.length < 20 ? "✍️ Write at least 20 characters first..." : "Run my code →"}
          </button>
        </div>
      )}

      {/* ── CELEBRATE ── */}
      {lessonPhase === "celebrate" && (
        <div style={{ background: "#fff", borderRadius: 18, padding: "24px", border: "1px solid #E7E5E4", textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 10 }}>✅</div>
          <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 24, fontWeight: 900, color: "#44403C", margin: "0 0 8px" }}>
            The whole crew is greeted! 👋
          </h2>
          <p style={{ fontSize: 14, color: "#78716C", lineHeight: 1.7, marginBottom: 20 }}>
            Your loop handled three different crews - that's the power of iteration. One loop, any number of items.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 22, textAlign: "left" }}>
            {(testResults && testResults.length ? testResults : [
              { crew: ["Alice", "Bob", "Carol"],    expected: ["Hello, Alice!", "Hello, Bob!", "Hello, Carol!"] },
              { crew: ["Solo"],                     expected: ["Hello, Solo!"] },
              { crew: ["Ash", "Jun", "Mia", "Leo"], expected: ["Hello, Ash!", "Hello, Jun!", "Hello, Mia!", "Hello, Leo!"] },
            ]).map((r, i) => (
              <div key={i} style={{ background: "#0f172a", borderRadius: 10, padding: "10px 14px", border: "1px solid #059669" }}>
                <div style={{ fontFamily: "'Courier New', monospace", fontSize: 12, color: "#9CDCFE", marginBottom: 6 }}>crew = {JSON.stringify(r.crew)}</div>
                {r.expected.map((l, j) => (
                  <div key={j} style={{ fontFamily: "'Courier New', monospace", fontSize: 13, color: "#86EFAC", lineHeight: 1.7 }}>{l}</div>
                ))}
              </div>
            ))}
          </div>

          <div style={{
            display: "inline-flex", alignItems: "center", gap: 12,
            background: "linear-gradient(135deg, #F5F3FF, #EDE9FE)",
            borderRadius: 14, padding: "14px 22px", marginBottom: 24, border: `1px solid ${color}40`,
          }}>
            <span style={{ fontSize: 28 }}>👋</span>
            <div style={{ textAlign: "left" }}>
              <div style={{ fontWeight: 800, color: "#5B21B6", fontSize: 18 }}>+120 XP earned!</div>
              <div style={{ fontSize: 12, color }}>Crew Caller badge unlocked 🏅</div>
            </div>
          </div>

          <div>
            <button onClick={handleComplete} style={{
              background: color, color: "#fff", border: "none", borderRadius: 14,
              padding: "14px 36px", cursor: "pointer", fontSize: 16, fontWeight: 800,
              boxShadow: `0 4px 20px ${color}40`,
            }}>Next Lesson →</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LESSON ────────────────────────────────────────────────────────────────────

function LessonView({ ageId, onNav, progress, setProgress, currentLessonId }) {
  // All hooks must be declared before any early returns (Rules of Hooks)
  const [phase, setPhase] = useState("try");
  const [attempt, setAttempt] = useState("");
  const [aiResponse, setAiResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [eyeBreak, setEyeBreak] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setEyeBreak(true), 1200000); // 20 real minutes
    return () => clearTimeout(t);
  }, []);

  if (ageId === "little") {
    const mods = getModulesWithStatus(ageId, progress);
    const activeId = mods.find(m => m.active)?.id ?? 1;
    const id = currentLessonId || activeId;
    if (id === 2) return <ExplorersLesson2 onNav={onNav} progress={progress} setProgress={setProgress} />;
    return <ExplorersLesson1 onNav={onNav} progress={progress} setProgress={setProgress} />;
  }
  if (ageId === "middle") {
    const mods = getModulesWithStatus(ageId, progress);
    const activeId = mods.find(m => m.active)?.id ?? 1;
    const id = currentLessonId || activeId;
    if (id === 2) return <BuildersLesson2 onNav={onNav} progress={progress} setProgress={setProgress} />;
    return <BuildersLesson1 onNav={onNav} progress={progress} setProgress={setProgress} />;
  }
  if (ageId === "teen") {
    const mods = getModulesWithStatus(ageId, progress);
    const activeId = mods.find(m => m.active)?.id ?? 1;
    const id = currentLessonId || activeId;
    if (id === 2) return <InnovatorsLesson2 onNav={onNav} progress={progress} setProgress={setProgress} />;
    return <InnovatorsLesson1 onNav={onNav} progress={progress} setProgress={setProgress} />;
  }

  const group = AGE_GROUPS.find(g => g.id === ageId);
  const progressPhase = { try: "think", hint: "think", ai: "check", celebrate: "celebrate" }[phase] || "watch";

  const handleAskAI = async () => {
    setLoading(true); setPhase("ai");
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514", max_tokens: 1000,
          system: `You are SproutCode, a warm coding guide for a ${ageId === "little" ? "6-8 year old" : ageId === "middle" ? "9-12 year old" : "14-17 year old"} child. RULES: Never write working code. Ask ONE guiding question. Keep response under 80 words. Be warm and encouraging. End with a question.`,
          messages: [{ role: "user", content: `Child is learning loops. Their attempt: "${attempt}". Guide them without giving the answer.` }]
        })
      });
      const data = await res.json();
      setAiResponse(data.content?.[0]?.text || "Great try! What part of the loop confuses you most? 🤔");
    } catch { setAiResponse("Excellent effort! Think of a loop like a spinning top — what tells it when to stop? What could you write to control that? 🌀"); }
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 660, margin: "0 auto", padding: "24px 20px" }}>
      <button onClick={() => onNav("dashboard")} style={{ background: "none", border: "none", cursor: "pointer", color: "#78716C", fontSize: 13, marginBottom: 18 }}>← Back to Dashboard</button>

      {eyeBreak && (
        <div style={{ background: "linear-gradient(135deg, #052e16, #14532d)", borderRadius: 18, padding: "20px 22px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <span style={{ fontSize: 32 }}>👁️</span>
            <div>
              <div style={{ fontWeight: 800, color: "#86efac", fontSize: 15 }}>20-20-20 Eye Break!</div>
              <div style={{ fontSize: 13, color: "#a7f3d0" }}>Look at something 20 feet away for 20 seconds. Your eyes need a rest! 🌿</div>
            </div>
          </div>
          <button onClick={() => setEyeBreak(false)} style={{ background: "#10B981", color: "#fff", border: "none", borderRadius: 10, padding: "8px 16px", cursor: "pointer", fontWeight: 700, flexShrink: 0 }}>Done ✓</button>
        </div>
      )}

      <div style={{ background: `linear-gradient(135deg, ${group.color}, ${group.color}CC)`, borderRadius: 20, padding: "22px 26px", marginBottom: 20, color: "#fff" }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>MODULE 3 · LOOPS</div>
        <div style={{ fontSize: 20, fontWeight: 800, fontFamily: "'Georgia', serif" }}>My First Loop</div>
      </div>

      <LessonProgress phase={progressPhase} color={group.color} />

      <div style={{ background: "#FEF3C7", borderRadius: 14, padding: "16px 18px", marginBottom: 18, border: "1px solid #F59E0B30" }}>
        <div style={{ fontWeight: 700, color: "#92400E", marginBottom: 6 }}>📖 The Concept</div>
        <p style={{ fontSize: 13, color: "#44403C", lineHeight: 1.7, margin: 0 }}>
          A <strong>loop</strong> tells your computer to repeat something. Like brushing each tooth in the same way — instead of writing 32 instructions, you write one and say "do this 32 times!"
        </p>
      </div>

      <div style={{ background: "#fff", borderRadius: 16, padding: "18px", border: "1px solid #E7E5E4", marginBottom: 16 }}>
        <div style={{ fontWeight: 700, color: "#44403C", marginBottom: 10, fontSize: 14 }}>🎯 Challenge: Print "Hello!" 5 times</div>
        <div style={{ background: "#FAFAF9", borderRadius: 10, padding: "12px 14px", fontFamily: "monospace", fontSize: 13, color: "#78716C", border: "1px dashed #D6D3D1", marginBottom: 14 }}>
          Write a for loop in Python...<br /><span style={{ color: "#A8A29E" }}># Your code here</span>
        </div>

        {phase === "try" && (
          <>
            <div style={{ background: "#FEF3C7", borderRadius: 10, padding: "10px 14px", marginBottom: 12, display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ fontSize: 16 }}>🧠</span>
              <div style={{ fontSize: 12, color: "#44403C" }}><strong>Brain First!</strong> Try it yourself — even a wrong answer helps you learn more than no answer. Write your best guess!</div>
            </div>
            <textarea value={attempt} onChange={e => setAttempt(e.target.value)} placeholder="e.g. for i in range(5):" style={{ width: "100%", minHeight: 90, borderRadius: 10, border: "2px solid #E7E5E4", padding: "10px 12px", fontFamily: "monospace", fontSize: 13, resize: "vertical", outline: "none", color: "#44403C", boxSizing: "border-box" }} />
            <button onClick={() => setPhase("hint")} disabled={attempt.length < 5} style={{ marginTop: 10, background: attempt.length >= 5 ? group.color : "#D6D3D1", color: "#fff", border: "none", borderRadius: 10, padding: "10px 20px", cursor: attempt.length >= 5 ? "pointer" : "default", fontWeight: 700, fontSize: 13, width: "100%" }}>
              {attempt.length < 5 ? "✍️ Write something first..." : "✅ I tried! Show hint →"}
            </button>
          </>
        )}

        {phase === "hint" && (
          <div>
            <div style={{ background: "#ECFDF5", borderRadius: 10, padding: "12px 14px", marginBottom: 12, border: "1px solid #10B98130" }}>
              <div style={{ fontWeight: 700, color: "#059669", marginBottom: 4 }}>💡 Hint Unlocked!</div>
              <div style={{ fontSize: 13, color: "#44403C" }}>In Python, <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>for</code> loops use <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>range()</code> to count. What number would you put inside <code style={{ background: "#fff", padding: "1px 5px", borderRadius: 4 }}>range()</code> to get 5 repetitions?</div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleAskAI} style={{ flex: 1, background: "#7C3AED", color: "#fff", border: "none", borderRadius: 10, padding: "10px", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>🤖 Ask AI Guide</button>
              <button onClick={() => setPhase("try")} style={{ background: "#F5F5F4", color: "#44403C", border: "none", borderRadius: 10, padding: "10px 16px", cursor: "pointer", fontSize: 13 }}>✏️ Try again</button>
            </div>
          </div>
        )}

        {phase === "ai" && (
          <div style={{ background: "#F5F3FF", borderRadius: 12, padding: "14px 16px", border: "1px solid #7C3AED20" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 18 }}>🌱</span>
              <span style={{ fontWeight: 700, color: "#7C3AED", fontSize: 13 }}>SproutCode AI Guide</span>
              <Badge color="#10B981" bg="#ECFDF5" small>✓ No answers given</Badge>
            </div>
            {loading ? <div style={{ color: "#78716C", fontStyle: "italic", fontSize: 13 }}>Thinking of a helpful hint... ✨</div>
              : <div style={{ fontSize: 13, color: "#44403C", lineHeight: 1.7 }}>{aiResponse}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PARENT VIEW ───────────────────────────────────────────────────────────────

function ParentView({ ageId, onNav, progress }) {
  const group = AGE_GROUPS.find(g => g.id === ageId);
  const modules = getModulesWithStatus(ageId, progress);
  const brainEntries = Object.values(progress.brainScores).filter(Boolean);
  const avgBrain = brainEntries.length > 0 ? Math.round(brainEntries.reduce((a, b) => a + b, 0) / brainEntries.length) : 0;
  const offlineActivities = OFFLINE_ACTIVITIES[ageId] || OFFLINE_ACTIVITIES.middle;

  return (
    <div style={{ maxWidth: 700, margin: "0 auto", padding: "24px 20px" }}>
      <button onClick={() => onNav("dashboard")} style={{ background: "none", border: "none", cursor: "pointer", color: "#78716C", fontSize: 13, marginBottom: 18 }}>← Back to Dashboard</button>

      <div style={{ background: "linear-gradient(135deg, #7C3AED, #6D28D9)", borderRadius: 20, padding: "22px 26px", marginBottom: 22, color: "#fff" }}>
        <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 4 }}>PARENT & GUARDIAN VIEW</div>
        <div style={{ fontFamily: "'Georgia', serif", fontSize: 22, fontWeight: 800 }}>Weekly Overview</div>
        <div style={{ fontSize: 13, opacity: 0.85, marginTop: 6 }}>Full visibility into screen time, learning, and offline balance</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12, marginBottom: 22 }}>
        {[
          { icon: "⏱️", label: "Avg Daily Screen", value: progress.completedModules.length > 0 ? `${Math.min(group.maxSession, Math.round(progress.completedModules.length * 8))} min` : "0 min", note: progress.completedModules.length > 0 ? `Under ${group.maxSession}m limit` : "No sessions yet", color: progress.completedModules.length > 0 ? "#10B981" : "#A8A29E" },
          { icon: "🧠", label: "Brain Score", value: brainEntries.length > 0 ? `${avgBrain}%` : "—", note: "Independent thinking", color: brainEntries.length > 0 ? (avgBrain > 75 ? "#10B981" : "#F59E0B") : "#A8A29E" },
          { icon: "🌿", label: "Offline Activities", value: progress.offlineActivities.length > 0 ? `${progress.offlineActivities.length} done` : "0 done", note: "This week", color: progress.offlineActivities.length > 0 ? "#059669" : "#A8A29E" },
          { icon: "🤖", label: "AI Prompts Used", value: "0", note: progress.completedModules.length > 0 ? "Always after own attempt" : "No prompts yet", color: "#A8A29E" },
        ].map((s, i) => (
          <div key={i} style={{ background: "#fff", borderRadius: 14, padding: "16px 18px", border: "1px solid #E7E5E4" }}>
            <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.color, fontFamily: "'Georgia', serif" }}>{s.value}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#44403C" }}>{s.label}</div>
            <div style={{ fontSize: 11, color: "#A8A29E", marginTop: 3 }}>{s.note}</div>
          </div>
        ))}
      </div>

      {/* Offline Activities Section */}
      <div style={{ background: "linear-gradient(135deg, #052e16, #14532d)", borderRadius: 18, padding: "22px", marginBottom: 22 }}>
        <h3 style={{ fontFamily: "'Georgia', serif", fontSize: 17, color: "#86efac", margin: "0 0 6px" }}>🌿 Upcoming Offline Activities</h3>
        <p style={{ fontSize: 13, color: "#a7f3d0", margin: "0 0 16px", lineHeight: 1.6 }}>
          When your child's session ends, SproutCode suggests these age-matched offline activities to balance screen time with movement and nature.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {offlineActivities.slice(0, 4).map((act, i) => (
            <div key={i} style={{ background: "rgba(255,255,255,0.07)", borderRadius: 12, padding: "12px 14px", display: "flex", gap: 12, alignItems: "flex-start", border: "1px solid rgba(255,255,255,0.12)" }}>
              <span style={{ fontSize: 24, flexShrink: 0 }}>{act.emoji}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 3 }}>
                  <span style={{ fontWeight: 700, color: "#fff", fontSize: 13 }}>{act.title}</span>
                  <span style={{ background: TYPE_BG[act.type], color: TYPE_COLORS[act.type], borderRadius: 99, padding: "1px 8px", fontSize: 10, fontWeight: 700 }}>{TYPE_LABEL[act.type]}</span>
                </div>
                <div style={{ fontSize: 12, color: "#a7f3d0", lineHeight: 1.5 }}>{act.desc}</div>
              </div>
              <div style={{ fontSize: 11, color: "#6ee7b7", flexShrink: 0 }}>~{act.mins}m</div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, fontSize: 12, color: "#6ee7b7", textAlign: "center" }}>
          + {offlineActivities.length - 4} more activities in your child's library
        </div>
      </div>

      {/* Brain scores */}
      <h3 style={{ fontSize: 14, fontWeight: 700, color: "#44403C", marginBottom: 12 }}>🧠 Brain Scores by Module</h3>
      {modules.filter(m => m.brainScore).map((m, i) => (
        <div key={i} style={{ background: "#fff", borderRadius: 12, padding: "12px 16px", border: "1px solid #E7E5E4", marginBottom: 8, display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#44403C" }}>{m.title}</div>
          <div style={{ width: 160 }}><BrainMeter score={m.brainScore} /></div>
        </div>
      ))}
    </div>
  );
}

// ─── APP ───────────────────────────────────────────────────────────────────────

// ─── FOUNDING FAMILIES PAGE ───────────────────────────────────────────────────

// Populate manually as families opt in. Shape: { firstName, lastInitial, ageGroup }
const FOUNDING_FAMILIES = [];

function FoundingFamiliesPage() {
  const [isNarrow, setIsNarrow] = useState(typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const h = () => setIsNarrow(window.innerWidth < 640);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const heading = { fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 800, lineHeight: 1.2 };
  const body = { fontFamily: "'Inter', system-ui, sans-serif", lineHeight: 1.7 };
  const linkStyle = { ...body, color: "#78716C", fontSize: 14, textDecoration: "none", fontWeight: 500 };

  return (
    <div style={{ ...body, minHeight: "100vh", background: "#052e16", display: "flex", flexDirection: "column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Playfair+Display:wght@600;700;800;900&display=swap');
      `}</style>

      {/* Header with logo, links back to / */}
      <div style={{ padding: "28px 32px" }}>
        <a href="/" style={{ display: "inline-flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
          <span style={{ fontSize: 22 }}>🌱</span>
          <span style={{ ...heading, fontSize: 19, color: "#fff", fontWeight: 700 }}>SproutCode</span>
        </a>
      </div>

      {/* Content */}
      <div style={{ flex: 1, maxWidth: 1120, width: "100%", margin: "0 auto", padding: isNarrow ? "40px 24px 72px" : "72px 24px 100px", boxSizing: "border-box" }}>
        <h1 style={{ ...heading, fontSize: isNarrow ? 36 : 52, color: "#fff", margin: "0 0 18px", textAlign: "center", letterSpacing: -0.5 }}>
          Founding Families
        </h1>
        <p style={{ ...body, fontSize: isNarrow ? 16 : 18, color: "#a7f3d0", textAlign: "center", margin: "0 auto 56px", maxWidth: 640 }}>
          The parents who believed early. Their families get founder pricing locked in for life when SproutCode launches paid plans.
        </p>

        {FOUNDING_FAMILIES.length === 0 ? (
          <div style={{ background: "#FFFBEB", borderRadius: 16, padding: isNarrow ? "32px 20px" : "48px 32px", textAlign: "center", border: "1px solid rgba(255,255,255,0.1)", maxWidth: 640, margin: "0 auto" }}>
            <p style={{ ...body, fontSize: isNarrow ? 15 : 17, color: "#1C1917", margin: 0 }}>
              The first Founding Families will be announced here soon.
            </p>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isNarrow ? "repeat(2, 1fr)" : "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
            {FOUNDING_FAMILIES.map((f, i) => (
              <div key={i} style={{ background: "#FFFBEB", borderRadius: 12, padding: "18px 20px", textAlign: "center", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ ...heading, fontSize: 18, color: "#1C1917", fontWeight: 700, marginBottom: 4 }}>{f.firstName} {f.lastInitial}</div>
                <div style={{ ...body, fontSize: 13, color: "#57534E" }}>{f.ageGroup}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ background: "#1C1917", borderTop: "1px solid #292524" }}>
        <div style={{
          maxWidth: 1120, margin: "0 auto", padding: "40px 24px",
          display: "flex",
          flexDirection: isNarrow ? "column" : "row",
          alignItems: isNarrow ? "center" : "flex-start",
          justifyContent: "space-between",
          gap: isNarrow ? 28 : 0,
          textAlign: isNarrow ? "center" : "left",
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: isNarrow ? "center" : "flex-start" }}>
              <span style={{ fontSize: 18 }}>🌱</span>
              <span style={{ ...heading, fontSize: 16, color: "#fff", fontWeight: 700 }}>SproutCode</span>
            </div>
            <p style={{ ...body, fontSize: 13, color: "#78716C", margin: "6px 0 0" }}>Built for the AI era.</p>
          </div>
          <div style={{ display: "flex", gap: 24 }}>
            <a href="https://www.instagram.com/sproutcodeapp" target="_blank" rel="noopener noreferrer" style={linkStyle}>Instagram</a>
            <a href="https://www.youtube.com/@SproutCodeApp" target="_blank" rel="noopener noreferrer" style={linkStyle}>YouTube</a>
            <a href="https://www.linkedin.com/company/sprout-code-app/" target="_blank" rel="noopener noreferrer" style={linkStyle}>LinkedIn</a>
          </div>
        </div>
        <div style={{ textAlign: "center", padding: "0 24px 32px" }}>
          <p style={{ ...body, fontSize: 12, color: "#57534E", margin: 0 }}>&copy; 2026 SproutCode</p>
        </div>
      </footer>
    </div>
  );
}

export default function App() {
  const [showLanding, setShowLanding] = useState(() => {
    try { return !localStorage.getItem("sproutcode_visited"); } catch { return true; }
  });
  const [view, setView] = useState("home");
  const [ageId, setAgeId] = useState(null);
  const [sessionMin, setSessionMin] = useState(0);
  const [progress, setProgress] = useState(DEFAULT_PROGRESS);
  const [currentLessonId, setCurrentLessonId] = useState(null);
  const timerRef = useRef(null);
  const [winW, setWinW] = useState(window.innerWidth);

  useEffect(() => {
    const h = () => setWinW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  useEffect(() => {
    const saved = loadProgress();
    if (saved) setProgress(saved);
  }, []);

  const handleEnterApp = () => {
    try { localStorage.setItem("sproutcode_visited", "true"); } catch {}
    setShowLanding(false);
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => setSessionMin(m => m + 1), 60000); // 60s = 1 real min
  };

  const selectAge = (id) => {
    setAgeId(id);
    setSessionMin(0);
    startTimer();
    setView("dashboard");
  };

  const handleSessionEnd = () => {
    clearInterval(timerRef.current);
    setView("offline");
  };

  const handleUnlock = () => {
    setSessionMin(0);
    startTimer();
    setView("dashboard");
  };

  useEffect(() => {
    if (ageId) {
      const group = AGE_GROUPS.find(g => g.id === ageId);
      if (sessionMin >= group.maxSession) handleSessionEnd();
    }
  }, [sessionMin, ageId]);

  const nav = (v, lessonId) => {
    if (v === "lesson") {
      setCurrentLessonId(lessonId !== undefined && lessonId !== null ? lessonId : null);
    }
    setView(v);
  };

  if (typeof window !== "undefined" && window.location.pathname === "/founding-families") {
    return <FoundingFamiliesPage />;
  }

  if (showLanding) return <Landing onEnterApp={handleEnterApp} />;

  return (
    <div style={{ minHeight: "100vh", background: view === "offline" ? "transparent" : "linear-gradient(180deg, #FFFBEB 0%, #FFF7ED 100%)", fontFamily: "'Trebuchet MS', 'Helvetica Neue', sans-serif" }}>
      {view !== "offline" && (
        <div style={{ background: "rgba(255,255,255,0.88)", backdropFilter: "blur(12px)", borderBottom: "1px solid #E7E5E4", padding: winW < 480 ? "8px 10px" : "11px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100, overflow: "hidden" }}>
          <button onClick={() => setView("home")} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, flexShrink: 0, minWidth: "fit-content", padding: "9px 12px" }}>
            <span style={{ fontSize: winW < 480 ? 16 : 20 }}>🌱</span>
            {winW >= 480 && <span style={{ fontFamily: "'Georgia', serif", fontWeight: 800, fontSize: 17, color: "#44403C" }}>SproutCode</span>}
          </button>
          <div style={{ display: "flex", gap: 4, flexShrink: 1, minWidth: 0 }}>
            {[{ id: "home", label: "Home", short: "Home" }, ...(ageId ? [{ id: "dashboard", label: "My Path", short: "Path" }, { id: "lesson", label: "Lesson", short: "Learn" }, { id: "parent", label: "👪 Parent", short: "👪" }] : [])].map(n => (
              <button key={n.id} onClick={() => nav(n.id)} style={{ background: view === n.id ? "#F59E0B" : "transparent", color: view === n.id ? "#fff" : "#44403C", border: "none", borderRadius: 8, padding: winW < 390 ? "4px 8px" : winW < 480 ? "4px 8px" : "5px 13px", cursor: "pointer", fontSize: winW < 390 ? 11 : winW < 480 ? 11 : 13, fontWeight: 600, flexShrink: 0, whiteSpace: "nowrap" }}>{winW < 480 ? n.short : n.label}</button>
            ))}
          </div>
        </div>
      )}

      {view === "home" && <HomeView onSelectAge={selectAge} />}
      {view === "dashboard" && ageId && <DashboardView ageId={ageId} onNav={nav} sessionMin={sessionMin} onSessionEnd={handleSessionEnd} progress={progress} />}
      {view === "lesson" && ageId && <LessonView ageId={ageId} onNav={nav} progress={progress} setProgress={setProgress} currentLessonId={currentLessonId} />}
      {view === "parent" && ageId && <ParentView ageId={ageId} onNav={nav} progress={progress} />}
      {view === "offline" && ageId && <OfflineScreen ageId={ageId} onUnlock={handleUnlock} />}
    </div>
  );
}
